import type {
    InventoryChangeEntry,
    InventoryRow,
    InventoryRowEdit,
    PermissionRole,
    Pool,
    PoolInfoDraft,
    PoolManagementModel,
    PoolPermission,
    PoolPermissionCandidate,
    PoolPermissionHolder,
    PoolManagementView,
    PoolStatus,
    PriceSummaryState,
    PricingResolutionCardState,
    PricingSimulatorState,
    StagedRowBaseline,
} from '.';
import { EVENT_EMAIL_PATTERN } from '.';
import {
    clearRowFieldEdit,
    clearStagedChanges,
    clearSubmitError,
    closeCreatePool,
    closeSubmitReview,
    closeUploadWizard,
    failCreatePool,
    failSubmit,
    failUploadApply,
    pushUploadStreamError,
    openCreatePool,
    startCreatePool,
    succeedCreatePool,
    updateCreatePoolDraft,
    initialPoolManagementState,
    openPoolDetail,
    openPoolList,
    openSubmitReview,
    openUploadWizard,
    poolManagementReducer,
    resetEdits,
    setActivePoolDetailTab,
    setAddSymbolDraft,
    setAppliedSymbolSearch,
    setChangeLogActionFilter,
    setChangeLogPage,
    setChangeLogRowsPerPage,
    setChangeLogSymbolFilter,
    setChangeLogTimeRange,
    setInventoryPage,
    setInventoryRowsPerPage,
    setPoolSearchQuery,
    setPoolStatusFilter,
    setSymbolSearchQuery,
    setUploadCategoryFilter,
    setUploadError,
    setUploadFile,
    setUploadMode,
    setUploadStep,
    setUploadSymbolFilter,
    stageRowEdit,
    stageRowRemoval,
    startCsvDownload,
    endCsvDownload,
    startSubmit,
    startUploadApply,
    advanceUploadProgress,
    succeedSubmit,
    succeedUploadApply,
    unstageRowRemoval,
} from './state';
import type {
    ChangeLogActionFilter,
    ChangeLogTimeRange,
    CreatePoolDraft,
    InventoryRowsPerPage,
    PoolDetailTab,
    PoolManagementAction,
    PoolManagementState,
    PoolStatusFilter,
    UploadApplyProgress,
    UploadStreamError,
    UploadCategory,
    UploadFileInfo,
    UploadMode,
    UploadWizardStep,
} from './state';
import { computed, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { QueryClient, QueryObserverOptions } from '@tanstack/query-core';
import type {
    PermissionDirective,
    PermissionType,
    PermissionUpdateEntry,
} from '@thinkalpha/common/contracts/permissions.js';
import { isSpecialPermissionSubject, isTypedPermissionDirective } from '@thinkalpha/common/contracts/permissions.js';
import { InventoryChangeKind, InventoryUploadErrorReason } from '@thinkalpha/locates-ws-client';
import type {
    AddPoolItemsResponse,
    ChangeLogEntry as ApiChangeLogEntry,
    GetPoolChangeLogResponse,
    InventoryItem,
    InventoryUploadError,
    ListPoolItemsResponse,
    NewPoolItem,
    SymbolPricesResponse,
} from '@thinkalpha/locates-ws-client';
import type { FirmMembershipWithProfile } from '@thinkalpha/ui-types/types/admin/firms';
import type { Provider } from '@thinkalpha/ui-types/types/admin/lab';
import type {
    BorrowStatusKey,
    PoolPricingConfig,
    PoolPricingMethod,
    PoolPricingSimulationResponse,
} from '@thinkalpha/ui-types/types/admin/lab-pool-pricing';
import type {
    InventoryPool,
    ListPoolsResponse,
    UpdatePoolContractRequest,
} from '@thinkalpha/ui-types/types/admin/lab-pools';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import debounce from 'lodash.debounce';
import { createChangeLogCsv } from 'src/admin/LAB/PoolManagement/poolChangeLogCsv';
import { createPoolListCsv } from 'src/admin/LAB/PoolManagement/poolListCsv';
import type { PoolListCsvColumnLabels } from 'src/admin/LAB/PoolManagement/poolListCsv';
import { isSamePoolPricingConfig } from 'src/admin/models/PoolManagementModel/pricing/configEquality';
import { DEFAULT_POOL_PRICING_CONFIG } from 'src/admin/models/PoolManagementModel/pricing/defaultConfig';
import { derivePricingResolution } from 'src/admin/models/PoolManagementModel/pricing/derivePricingResolution';
import { previewDraftSimulation } from 'src/admin/models/PoolManagementModel/pricing/previewDraftSimulation';
import { diffInventory } from 'src/admin/models/PoolManagementModel/upload/diffInventory';
import type { UploadDiff } from 'src/admin/models/PoolManagementModel/upload/diffInventory';
import { openInventoryUploadStream } from 'src/admin/models/PoolManagementModel/upload/inventoryUploadStream';
import {
    createInventoryCsv,
    createInventoryTemplateCsv,
    parseInventoryCsv,
} from 'src/admin/models/PoolManagementModel/upload/parseInventoryCsv';
import type { InventoryParseResult } from 'src/admin/models/PoolManagementModel/upload/parseInventoryCsv';
import { inject, injectable } from 'src/features/ioc';
import { legacyLabPools } from 'src/lib/featureFlags/flags/legacyLabPools';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getFirmMembersQuery } from 'src/queries/firms';
import {
    getAllPoolChangeLogQuery,
    getAllPoolItemsQuery,
    getAllPoolsQuery,
    getFirmPoolsQuery,
    getPoolChangeLogQuery,
    getPoolItemPricingQuery,
    getPoolItemsQuery,
    getPoolPricingSimulationQuery,
    getPricingSimulationQuery,
    getSymbolPricesQuery,
    invalidatePoolChangeLogQuery,
    invalidatePoolItemsQuery,
    invalidatePoolsQuery,
    LAB_POOLS,
} from 'src/queries/labPools';
import {
    addPoolItems,
    createPool,
    deletePool,
    removePoolItem,
    updatePool,
    updatePoolItem,
    updatePoolPermissionsForEntity,
} from 'src/queries/labPoolsMutations';
import { getAllProvidersQuery } from 'src/queries/labProviders';
import {
    getLocatesAuthPayloadFromState,
    getLocatesSocketConnectionSettingsFromState,
} from 'src/store/selectors/locates/locatesSocket';
import type { ReactBindings } from 'src/types/bindings';

const PENDING_ROW_PREFIX = '__pending__';

/**
 * `true` when the row id was minted locally for a not-yet-submitted addition.
 * Exposed so views can render draft rows with distinct visual treatment.
 */
export const isDraftRowId = (id: string): boolean => id.startsWith(PENDING_ROW_PREFIX);

/**
 * Minimum trimmed length of the justification the delete-pool endpoint
 * accepts. Mirrors locates-ws's `MIN_DELETE_REASON_LENGTH`; enforced
 * client-side so the confirm button can stay disabled instead of
 * round-tripping a guaranteed 400.
 */
export const MIN_DELETE_POOL_REASON_LENGTH = 20;

/**
 * Idle window between a simulator input (symbol keystroke, hierarchy drag,
 * minimum-price edit) and the simulation run it schedules. Long enough to
 * coalesce a typing burst, short enough that the preview still reads as
 * live.
 */
const SIMULATOR_DEBOUNCE_MS = 300;

/**
 * One leg of the fan-out submit. `kind` identifies what staged change
 * the op represents so the post-`Promise.allSettled` walk can clear
 * exactly the succeeded ones (leaving failures staged for retry).
 */
type SubmitOperation =
    | { kind: 'update'; rowId: string; promise: Promise<unknown> }
    | { kind: 'remove'; rowId: string; promise: Promise<unknown> }
    /**
     * Carries the pending-row ids that participated in the batch so a
     * successful POST can clear their staged `rowEdits` entries alongside
     * dropping them from `#pendingAddsByPool`. Without this, edits keyed
     * by `PENDING_ROW_PREFIX...` would be orphaned in `rowEdits` after a
     * partial-failure retry and leave the model invisibly dirty.
     */
    | { kind: 'add-batch'; rowIds: string[]; promise: Promise<unknown> };

/**
 * Upload size cap (8 MiB). The parser is fully synchronous and runs on
 * the main thread, so an unbounded `file.text()` on a several-hundred-
 * megabyte CSV would lock up the UI. 8 MiB is generous for the
 * 10k-row CSV the wizard advertises (~700 KB typical) and still small
 * enough to parse without measurable jank.
 */
const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;

/**
 * Debounce window before a symbol-search keystroke is committed to the
 * backend `symbol` query parameter. Keeps live typing to a single
 * request once the user pauses rather than one exact-match request per
 * keystroke.
 */
const SYMBOL_SEARCH_DEBOUNCE_MS = 300;

/**
 * Client-side shape check for the create-pool notification email; real
 * validation belongs to the backend, {@link EVENT_EMAIL_PATTERN} just
 * catches obvious typos before the POST. Shared with the Info tab's
 * edit flow so one field can't have two divergent validators — and it
 * deliberately matches the backend's own pattern rather than something
 * stricter, so a backend-accepted stored email never blocks edits.
 */

/**
 * Idle window for the live upload stream: an apply keeps waiting while
 * items are actively arriving and only finishes early if the stream goes
 * quiet this long without reaching the accepted count — a safety net for
 * a dropped event, without cutting off a large in-flight batch.
 */
const UPLOAD_STREAM_IDLE_MS = 10_000;

/**
 * Extra time to hold the upload stream's error listener open when the
 * item wait idles out SHORT of the expected count with no claimed error —
 * the suspicious case where a trailing `items-failed` may still be in
 * flight. Disposing immediately would drop it and report a false success.
 */
const UPLOAD_STREAM_ERROR_GRACE_MS = 3_000;

/**
 * How long to wait for the upload stream to authenticate and join the pool
 * room before giving up on live progress. If it isn't ready in this window
 * (missing/rejected token, no connection), the apply falls back to a single
 * jump by the backend's accepted count rather than blocking on item events.
 */
const UPLOAD_STREAM_READY_MS = 5_000;

/**
 * How long the progress bar is held at 100% once the apply finishes,
 * before the wizard swaps to the done screen, so the fill visibly
 * completes rather than snapping away mid-animation.
 */
const UPLOAD_COMPLETE_HOLD_MS = 300;

/**
 * Collapse whitespace and strip filesystem-reserved + control
 * characters from a string so it's safe to drop into a CSV download
 * filename. Windows is the strictest target (`<>:"/\|?*`), so we
 * sanitize for that set and let the cross-platform underscore
 * fallback do the rest. We also trim trailing dots/spaces and prefix
 * an underscore on Windows reserved device names (CON, PRN, AUX, NUL,
 * COM1..9, LPT1..9), both of which produce unusable filenames there.
 */
function sanitizeFilenameSegment(name: string): string {
    const sanitized = name
        .trim()
        .replace(/[<>:"/\\|?*\x00-\x1F]/g, '_')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/[. ]+$/, '');
    return /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i.test(sanitized) ? `_${sanitized}` : sanitized;
}

function timeRangeCutoffMs(range: ChangeLogTimeRange): number | null {
    if (range === 'custom') return null;
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    if (range === 'today') return start.getTime();
    const days = range === 'last-7-days' ? 7 : 30;
    // Subtract `days - 1` so "last 7 days" covers today plus the previous
    // six calendar days, matching typical user mental model.
    start.setDate(start.getDate() - (days - 1));
    return start.getTime();
}

/**
 * Action filter options offered for a modern pool: every change kind the
 * modern journal enumerates. Legacy pools only ever record quantity
 * movements — the backend maps their journal onto the modern enumeration
 * in the single `increment` case — so an action filter carries no real
 * signal there: they get just `'all'`, and the view hides the dropdown
 * when there is nothing to choose.
 */
const MODERN_CHANGE_LOG_ACTION_FILTER_VALUES: readonly ChangeLogActionFilter[] = [
    'all',
    ...Object.values(InventoryChangeKind),
];
const LEGACY_CHANGE_LOG_ACTION_FILTER_VALUES: readonly ChangeLogActionFilter[] = ['all'];

/**
 * Build the OData `$filter` sent to the change-log endpoint from the active
 * symbol / action / time-range filters, joined with `and`, matching the
 * clause grammar the backend's `validateChangeLogFilter` reduces to journal
 * params (it ignores the whole expression, with a warning, on anything else):
 *   - time:   `timestamp ge '<local-start-of-day ISO>'` (date literal must be quoted)
 *   - action: `kind eq '<InventoryChangeKind>'` (exact change kind)
 *   - symbol: `symbol eq '<UPPER>'` (exact match; `contains()` is rejected)
 * The action filter is passed in separately from the raw state because the
 * model coerces it per pool (legacy pools only support a subset of kinds).
 * Returns `undefined` when nothing is active. Filtering is delegated to the
 * backend so it spans the whole log and stays consistent with pagination.
 */
function buildChangeLogFilter(state: PoolManagementState, actionFilter: ChangeLogActionFilter): string | undefined {
    const clauses: string[] = [];
    const cutoff = timeRangeCutoffMs(state.changeLogTimeRange);
    if (cutoff !== null) clauses.push(`timestamp ge '${new Date(cutoff).toISOString()}'`);
    if (actionFilter !== 'all') clauses.push(`kind eq '${actionFilter}'`);
    const symbol = state.changeLogSymbolFilter.trim();
    // Escape single quotes per OData string-literal rules (doubled).
    if (symbol) clauses.push(`symbol eq '${symbol.toUpperCase().replace(/'/g, "''")}'`);
    return clauses.length ? clauses.join(' and ') : undefined;
}

function fromApiChangeLogEntry(entry: ApiChangeLogEntry): InventoryChangeEntry {
    const ms = Date.parse(entry.timestamp);
    const userId = entry.userId;
    return {
        id: entry.id,
        timestamp: Number.isFinite(ms) ? ms : 0,
        userId,
        userName: userId ?? 'System',
        orderId: entry.orderId,
        kind: entry.kind,
        symbol: entry.symbol,
        quantity: entry.quantity,
        price: entry.price,
        available: entry.available,
    };
}

/**
 * The backend's `PermissionType` is the four-rung ladder
 * `reader | writer | owner | admin`. The pool UI only cares about
 * the read/write/own gating, so admin collapses to `owner`.
 */
function fromEffectiveAccess(access: PermissionType): PoolPermission {
    return access === 'admin' ? 'owner' : access;
}

function fromApiItem(item: InventoryItem): InventoryRow {
    // Cleared prices come back as `null` (per backend "clear via PATCH
    // price: null" semantics). The current designs render the table cell
    // as a numeric input with no "no price" affordance, so coerce to 0
    // at the boundary. If/when we want a distinct "no price set" cell,
    // widen `InventoryRow.currentLabPrice` to `number | null` instead.
    //
    // `borrowStatus` and `quantityUsed` are typed on the wire today.
    // `dailyRevenue` is not yet in the swagger; the boundary cast picks
    // it up the moment it ships without further plumbing.
    const raw = item as unknown as Record<string, unknown>;
    const dailyRevenue = typeof raw.dailyRevenue === 'number' ? raw.dailyRevenue : null;
    return {
        id: item.id,
        symbol: item.symbol,
        currentQuantity: item.quantity,
        currentLabPrice: item.price ?? 0,
        borrowStatus: item.borrowStatus ?? null,
        quantityUsed: item.quantityUsed ?? null,
        dailyRevenue,
        available: item.available,
        currentPrice: item.currentPrice ?? null,
        fairValuePrice: item.fairValuePrice ?? null,
    };
}

function toBaseline(row: InventoryRow | undefined): StagedRowBaseline | undefined {
    // Draft rows carry their own data and render as additions; baselines
    // only describe server rows.
    if (!row || isDraftRowId(row.id)) return undefined;
    return { symbol: row.symbol, currentQuantity: row.currentQuantity, currentLabPrice: row.currentLabPrice };
}

/**
 * Order roles by privilege so the de-dup step in
 * {@link collectPoolPermissionHolders} keeps the most-privileged tier
 * an entity holds. The numeric values are arbitrary — only the
 * relative ordering matters.
 */
const ROLE_RANK: Record<PermissionRole, number> = {
    reader: 1,
    writer: 2,
    owner: 3,
    admin: 4,
};

/**
 * Extract the user UUIDs (and nothing else) from a single
 * `PermissionDirective` array. The Permissions tab is scoped to firm
 * members; group / firm / special / deny directives all return null
 * so they're filtered out at the top level.
 */
function userIdFromDirective(directive: PermissionDirective): string | null {
    if (isTypedPermissionDirective(directive)) {
        if (directive.mode !== 'allow') return null;
        return directive.type === 'user' ? directive.id : null;
    }
    if (typeof directive === 'string') {
        // Bare-string directives carry no type on the wire. We assume
        // any non-special UUID is a user — special pseudo-subjects
        // (`everyone`, etc.) are intentionally excluded.
        return isSpecialPermissionSubject(directive) ? null : directive;
    }
    return null;
}

/**
 * Walk a pool's `AppliedPermissions` and produce one row per unique
 * user that holds at least one allow directive. Group/firm/special
 * subjects are filtered out — the Permissions tab is firm-members-only.
 * Each row gets the user's highest rung; `userName` and `email` are
 * resolved through the firm-members directory, and users that don't
 * resolve are dropped from the list (UUIDs are never shown).
 */
function collectPoolPermissionHolders(
    pool: InventoryPool | undefined,
    memberLookup: ReadonlyMap<string, { firstName: string; lastName: string; email: string }>,
): ReadonlyArray<PoolPermissionHolder> {
    if (!pool) return [];
    const highestByUser = new Map<string, PermissionRole>();

    const consume = (directives: PermissionDirective[], role: PermissionRole) => {
        for (const directive of directives) {
            const userId = userIdFromDirective(directive);
            if (!userId) continue;
            const existing = highestByUser.get(userId);
            if (existing && ROLE_RANK[existing] >= ROLE_RANK[role]) continue;
            highestByUser.set(userId, role);
        }
    };
    consume(pool.permissions.admins, 'admin');
    consume(pool.permissions.owners, 'owner');
    consume(pool.permissions.writers, 'writer');
    consume(pool.permissions.readers, 'reader');

    const out: PoolPermissionHolder[] = [];
    for (const [userId, role] of highestByUser) {
        const profile = memberLookup.get(userId);
        if (!profile) continue;
        const userName = `${profile.firstName} ${profile.lastName}`.trim() || profile.email;
        out.push({ userId, userName, email: profile.email, role });
    }
    return out;
}

const EMPTY_POOL_INFO: PoolInfoDraft = { name: '', description: '', eventEmail: '' };

function poolInfoFrom(pool: Pool | null): PoolInfoDraft {
    if (!pool) return EMPTY_POOL_INFO;
    return { name: pool.name, description: pool.description ?? '', eventEmail: pool.eventEmail };
}

function isSamePoolInfo(a: PoolInfoDraft, b: PoolInfoDraft): boolean {
    return a.name === b.name && a.description === b.description && a.eventEmail === b.eventEmail;
}

function fromApiPool(p: InventoryPool, statusOverride?: PoolStatus): Pool {
    return {
        id: p.id,
        name: p.name,
        description: p.description,
        firmId: p.firmId,
        eventEmail: p.eventEmail,
        permission: fromEffectiveAccess(p.permissions.effectiveAccess),
        // Backend exposes a boolean `active`; the UI surfaces a richer
        // `'active' | 'paused'` enum so future statuses (e.g. `draining`)
        // can land without re-keying templates. The override map only
        // wins while an optimistic toggle is in flight.
        status: statusOverride ?? (p.active ? 'active' : 'paused'),
        pricing: p.pricing ?? null,
    };
}

@injectable()
export class PoolManagementModelImpl implements PoolManagementModel {
    readonly #disposableStack = new DisposableStack();

    // --- Reducer-backed state ---
    readonly #state = signal<PoolManagementState>(initialPoolManagementState);

    /**
     * Firm scope. `undefined` = not yet initialized (queries disabled);
     * `null` = explicitly superuser scope (list-all); `string` = firm-scoped.
     */
    readonly #firmId = signal<string | null | undefined>(undefined);

    /**
     * `firmId → firmName` lookup, owned here so the search filter and
     * the detail view can read through it. `null` outside the superuser
     * view (where the firm-scoped page subtitle already names the firm).
     */
    readonly #firmNamesByFirmId = signal<Readonly<Record<string, string>> | null>(null);

    /**
     * Display name of the scoped firm in the firm-scoped view (where
     * {@link #firmNamesByFirmId} is null by design). Used so the CSV
     * export's firm column shows the name instead of the raw firm id.
     */
    readonly #firmName = signal<string | null>(null);

    /**
     * Locally-added rows that have not yet been persisted. Keyed by
     * pool id so swapping pools doesn't lose the user's pending adds.
     * The row id starts with {@link PENDING_ROW_PREFIX} so the reducer
     * can distinguish "drop the pending add" from "stage backend removal".
     */
    readonly #pendingAddsByPool = signal<ReadonlyMap<string, ReadonlyArray<InventoryRow>>>(new Map());

    /**
     * Pool-status overrides, keyed by pool id. Pool status is derived
     * from `InventoryPool.active`; this map holds short-lived UI
     * overrides during an optimistic Pause/Resume PATCH so the pill
     * never flashes back to the pre-toggle value between the request
     * and the cache patch. On success the entry is removed so the UI
     * falls back to the canonical `InventoryPool.active` (which the
     * success path also patches into the React-Query cache); on
     * failure the entry rolls back to the previous override. Survives
     * pool-switch but not page reloads.
     */
    readonly #poolStatusOverrides = signal<ReadonlyMap<string, PoolStatus>>(new Map());

    /**
     * Per-pool monotonic counter. Each `#setPoolStatus` call bumps the
     * version for `poolId`; success and error handlers only apply their
     * effects when their captured version still matches. Without this,
     * a fast pause → resume → pause sequence whose API calls settle out
     * of order can roll the optimistic state back to a stale value.
     */
    readonly #poolStatusMutationVersion = new Map<string, number>();

    /**
     * Item-availability overrides, keyed by row id — the per-item analog
     * of {@link #poolStatusOverrides}. An entry exists exactly while an
     * availability PATCH is in flight (so it doubles as the "pending" set
     * that disables the row's toggle); success clears it after the items
     * refetch reseats `available`, failure rolls it back immediately.
     */
    readonly #itemAvailabilityOverrides = signal<ReadonlyMap<string, boolean>>(new Map());

    /**
     * Per-row monotonic counter mirroring {@link #poolStatusMutationVersion}:
     * out-of-order settlement of a fast suspend → publish sequence must not
     * roll the optimistic state back to a stale value.
     */
    readonly #itemAvailabilityMutationVersion = new Map<string, number>();

    /**
     * Pool currently awaiting a pause-confirmation. Non-null while the
     * confirm modal is open; the wizard clears it on cancel/confirm.
     */
    readonly #pendingPauseId = signal<string | null>(null);

    /**
     * Delete-pool dialog state. `#pendingDeleteId` is non-null while
     * the confirm dialog is open; the reason and error travel with it
     * so a failed DELETE keeps the user's typed justification for a
     * correct-and-retry loop.
     */
    readonly #pendingDeleteId = signal<string | null>(null);
    readonly #deletePoolReason = signal<string>('');
    readonly #isDeletingPool = signal<boolean>(false);
    readonly #deletePoolError = signal<string | null>(null);

    /**
     * Create-with-upload intent: id of a just-created pool whose detail
     * view should open with the upload wizard already up. Consumed by
     * the next {@link openPoolDetail} — after its OPEN_POOL_DETAIL
     * transition has scrubbed any stale wizard state — and dropped by
     * any other navigation. Plain field; nothing renders from it.
     */
    #pendingUploadWizardPoolId: string | null = null;

    /**
     * Wall-clock ms of the most recent point at which the open pool's
     * view became fresh: either a manual `refresh()` settled or a
     * successful mutation (submit, upload, pool status toggle,
     * permissions change) finished invalidating its queries. The
     * toolbar's refresh stamp reads through this so it ticks "just
     * now" automatically after every pool change.
     */
    readonly #lastRefreshAt = signal<number>(Date.now());

    /**
     * Stages a role change for the Permissions tab confirm dialog.
     * Carries the pool id captured at request time so the confirm
     * targets the original pool even if the user switches selection
     * before pressing Apply. Null when no confirm is pending.
     */
    readonly #pendingPermissionChange = signal<{
        poolId: string;
        userId: string;
        role: PermissionRole;
    } | null>(null);

    /**
     * In-flight permission mutations counter so the view can disable
     * controls while a PUT is settling. A counter (not a boolean)
     * because we may fan out multiple calls from a single user action
     * (e.g. revoke all tiers for a holder).
     */
    readonly #permissionsMutationsInFlight = signal<number>(0);

    /**
     * Discard-guard callbacks. `#pendingDiscard` holds the action that
     * runs when the user confirms; `#pendingDiscardCancel` (optional)
     * runs when they cancel. Callback-based rather than typed-intent so
     * the React shim's route blocker can park its `proceed` / `reset`
     * pair here without the model knowing about react-router.
     */
    readonly #pendingDiscard = signal<(() => void) | null>(null);
    readonly #pendingDiscardCancel = signal<(() => void) | null>(null);

    // --- Query observers ---
    readonly #poolsQuery: QueryObserverModel<ListPoolsResponse, Error, ListPoolsResponse>;
    readonly #itemsQuery: QueryObserverModel<ListPoolItemsResponse, Error, ListPoolItemsResponse>;
    /**
     * Full-pool inventory snapshot for the upload wizard's diff
     * (PLAT-12841): a CSV must be compared against every row, and the
     * paged `#itemsQuery` only holds the current table page. Enabled
     * only while the wizard is open so the full-pool load isn't paid on
     * every pool switch. The submit-review dialog no longer uses it —
     * it renders from `stagedRowBaselines` (PLAT-12889).
     */
    readonly #fullPoolItemsQuery: QueryObserverModel<ListPoolItemsResponse, Error, ListPoolItemsResponse>;
    readonly #changeLogQuery: QueryObserverModel<GetPoolChangeLogResponse, Error, GetPoolChangeLogResponse>;
    /**
     * Firm members of the selected pool's firm — only fetched once the
     * Permissions tab is active, to keep the directory request lazy.
     * The Permissions tab uses this to resolve user UUIDs to names and
     * to populate the grant-access selector.
     */
    readonly #firmMembersQuery: QueryObserverModel<
        ReadonlyArray<FirmMembershipWithProfile>,
        Error,
        ReadonlyArray<FirmMembershipWithProfile>
    >;
    readonly #queryClient: QueryClient;
    readonly #store: ReactBindings['Store'];
    readonly #currentUser: ReactBindings['CurrentUserModel'];
    readonly #legacyPoolIds: ReadonlySignal<string[]>;

    constructor(
        @inject('QueryObserverModelFactory')
        queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('QueryClient') queryClient: QueryClient,
        @inject('Store') store: ReactBindings['Store'],
        @inject('CurrentUserModel') currentUser: ReactBindings['CurrentUserModel'],
        @inject('FeatureFlagService') featureFlagService: ReactBindings['FeatureFlagService'],
    ) {
        this.#queryClient = queryClient;
        this.#store = store;
        this.#currentUser = currentUser;
        this.#legacyPoolIds = featureFlagService.getSignal(legacyLabPools);
        const poolsOptions = computed<QueryObserverOptions<ListPoolsResponse, Error, ListPoolsResponse>>(() => {
            const firmId = this.#firmId.value;
            // `null` is the explicit "superuser, list all" mode; `undefined`
            // means the page hasn't told us which firm yet, so disable the
            // firm-scoped query until it does.
            if (firmId === null) return getAllPoolsQuery();
            return getFirmPoolsQuery(firmId);
        });
        this.#poolsQuery = queryObserverModelFactory<ListPoolsResponse, Error, ListPoolsResponse>(poolsOptions);
        this.#disposableStack.use(this.#poolsQuery);

        const itemsOptions = computed<QueryObserverOptions<ListPoolItemsResponse, Error, ListPoolItemsResponse>>(() => {
            const s = this.#state.value;
            const poolId = s.selectedPoolId ?? undefined;
            // A symbol search is answered by the backend: pass the
            // committed term as the `symbol` parameter. The endpoint
            // returns every matching item and ignores skip/limit, so we
            // don't page it here.
            const search = s.appliedSymbolSearch.trim();
            if (search !== '') {
                return getPoolItemsQuery(poolId, { symbol: search });
            }
            // "All" rows-per-page loads the full pool (the Daily Total
            // summary needs every row), paging the backend for real
            // rather than requesting one oversized limit. Otherwise page
            // server-side with the current page / page-size.
            if (s.inventoryRowsPerPage === 'all') {
                return getAllPoolItemsQuery(poolId);
            }
            const perPage = s.inventoryRowsPerPage;
            // Guard against NaN / Infinity / fractional page state — the
            // reducer doesn't constrain `SET_INVENTORY_PAGE` callers, so
            // a bad caller could otherwise propagate a garbage `skip`
            // into the request. Display-side clamping against
            // `inventoryPageCount` would close the loop on stale rows
            // when total shrinks, but reading the count here creates a
            // signal cycle (the count comes from this very query).
            const requested = s.inventoryPage;
            const page = Number.isFinite(requested) ? Math.max(1, Math.trunc(requested)) : 1;
            return getPoolItemsQuery(poolId, { skip: (page - 1) * perPage, limit: perPage });
        });
        this.#itemsQuery = queryObserverModelFactory<ListPoolItemsResponse, Error, ListPoolItemsResponse>(itemsOptions);
        this.#disposableStack.use(this.#itemsQuery);

        const fullPoolItemsOptions = computed<
            QueryObserverOptions<ListPoolItemsResponse, Error, ListPoolItemsResponse>
        >(() => {
            const s = this.#state.value;
            // Upload wizard only: the CSV diff genuinely needs every row.
            // The submit-review dialog used to enable this too, but it now
            // renders from `stagedRowBaselines` captured at staging time, so
            // opening it no longer re-pages the whole pool (PLAT-12889).
            const poolId = s.uploadWizardOpen ? (s.selectedPoolId ?? undefined) : undefined;
            return getAllPoolItemsQuery(poolId);
        });
        this.#fullPoolItemsQuery = queryObserverModelFactory<ListPoolItemsResponse, Error, ListPoolItemsResponse>(
            fullPoolItemsOptions,
        );
        this.#disposableStack.use(this.#fullPoolItemsQuery);

        const changeLogOptions = computed<
            QueryObserverOptions<GetPoolChangeLogResponse, Error, GetPoolChangeLogResponse>
        >(() => {
            const s = this.#state.value;
            // Only fetch when the user is actually viewing the change-log
            // tab — the audit log is large enough that we don't want to
            // refresh it eagerly alongside the inventory grid.
            if (s.activePoolDetailTab !== 'change-log') {
                return getPoolChangeLogQuery(undefined);
            }
            const poolId = s.selectedPoolId ?? undefined;
            const perPage = s.changeLogRowsPerPage;
            // Symbol / action / time-range filters are sent to the backend as a
            // combined OData `$filter` so the server filters + paginates the log
            // together (the UI does not filter client-side). The action filter
            // goes through the per-pool coercion (legacy pools support only a
            // subset of kinds).
            const filter = buildChangeLogFilter(s, this.changeLogActionFilter.value);
            // "all" pages the whole filtered log via real backend pagination;
            // virtualization keeps the DOM flat. Otherwise fetch a single
            // page via skip/limit. We read the raw (unclamped) `changeLogPage`
            // here so reading the clamped value — which derives from this
            // query's `count` — can't form a cycle; a separate effect snaps the
            // stored page back into range once `count` is known.
            if (perPage === 'all') {
                return getAllPoolChangeLogQuery(poolId, { filter });
            }
            const requested = s.changeLogPage;
            const page = Number.isFinite(requested) ? Math.max(1, Math.trunc(requested)) : 1;
            return getPoolChangeLogQuery(poolId, { skip: (page - 1) * perPage, limit: perPage, filter });
        });
        this.#changeLogQuery = queryObserverModelFactory<GetPoolChangeLogResponse, Error, GetPoolChangeLogResponse>(
            changeLogOptions,
        );
        this.#disposableStack.use(this.#changeLogQuery);

        // Once the backend reports `count`, snap an out-of-range stored
        // change-log page back into range so the fetched slice matches the
        // displayed (clamped) page instead of requesting an empty skip. Uses
        // only private fields (no public computed) so it is safe to run during
        // construction; dispatching the clamped page is idempotent (re-running
        // sees `page === max` and stops).
        this.#disposableStack.defer(
            effect(() => {
                const s = this.#state.value;
                if (s.activePoolDetailTab !== 'change-log' || s.changeLogRowsPerPage === 'all') return;
                // Only clamp once a real count is in. While the new page is
                // still loading the query exposes `data === undefined`; treating
                // that as 0 would snap back to page 1 and block paging forward.
                const count = this.#changeLogQuery.signal.value.data?.count;
                if (count === undefined) return;
                const max = Math.max(1, Math.ceil(count / s.changeLogRowsPerPage));
                if (s.changeLogPage > max) this.#dispatch(setChangeLogPage(max));
            }),
        );

        const firmMembersOptions = computed<
            QueryObserverOptions<
                ReadonlyArray<FirmMembershipWithProfile>,
                Error,
                ReadonlyArray<FirmMembershipWithProfile>
            >
        >(() => {
            const s = this.#state.value;
            // The members directory is only needed by the Permissions tab.
            // Disable the query when the tab isn't open; the firm-id key
            // means the cache is shared with any other admin surface that
            // already fetched it.
            const pool = s.selectedPoolId
                ? this.#poolsQuery.signal.value.data?.pools.find((p) => p.id === s.selectedPoolId)
                : undefined;
            // Only fire the firm-members query when the Permissions tab
            // is actually accessible: stored tab is `permissions` AND the
            // caller has superuser access. Non-superusers store
            // `permissions` as their default but can't see the tab, so
            // the directory fetch would otherwise be wasted.
            const showsPermissions = s.activePoolDetailTab === 'permissions' && this.canManagePermissions.value;
            const firmId = showsPermissions ? pool?.firmId : undefined;
            return getFirmMembersQuery(firmId);
        });
        this.#firmMembersQuery = queryObserverModelFactory<
            ReadonlyArray<FirmMembershipWithProfile>,
            Error,
            ReadonlyArray<FirmMembershipWithProfile>
        >(firmMembersOptions);
        this.#disposableStack.use(this.#firmMembersQuery);

        // When `canManagePermissions` flips off (superuser session
        // expires or is toggled off externally) any in-flight permission-
        // change confirmation must be dropped — the dialog would
        // otherwise sit open with a callback that re-enters
        // `#putUserPermission` even though the tab is now hidden.
        const disposeSuperuserWatcher = effect(() => {
            if (!this.canManagePermissions.value) {
                this.#pendingPermissionChange.value = null;
            }
        });
        this.#disposableStack.defer(disposeSuperuserWatcher);
        // Drop any pending debounced symbol search so it can't dispatch
        // into a disposed model.
        this.#disposableStack.defer(() => this.#applySymbolSearch.cancel());

        // Pricing config, pool info draft, and simulator state are per-pool.
        // The baselines seed from the pool payload (default config while the
        // pool has no pricing), and reseat when the server copy changes
        // under a clean draft (PLAT-12937, PLAT-12970).
        let pricingPoolId: string | null = null;
        // The last pool-payload info copy this effect has seen, so reseats
        // key off SERVER changes only — comparing against `#poolInfoSaved`
        // would re-fire when a submit echo moves the baseline and clobber
        // it back to the stale cache copy.
        let lastServerInfo: PoolInfoDraft | null = null;
        this.#disposableStack.defer(
            effect(() => {
                const poolId = this.selectedPoolId.value;
                const pool = this.selectedPool.value;
                const serverPricing = pool?.pricing ?? null;
                const serverInfo = poolInfoFrom(pool);
                if (poolId !== pricingPoolId) {
                    pricingPoolId = poolId;
                    const saved = serverPricing ?? DEFAULT_POOL_PRICING_CONFIG;
                    this.#poolPricingSaved.value = saved;
                    this.#poolPricingDraft.value = saved;
                    this.#poolHasServerPricing.value = serverPricing !== null;
                    lastServerInfo = pool === null ? null : serverInfo;
                    this.#poolInfoSaved.value = serverInfo;
                    this.#poolInfoDraft.value = serverInfo;
                    this.#pricingConfirmOpen.value = false;
                    this.#simulatorSymbol.value = '';
                    this.#simulatorState.value = null;
                    // Orphan any in-flight simulation for the previous pool —
                    // its settlement would otherwise pass the token check and
                    // rewrite state after this reset.
                    this.#simulatorRun++;
                    this.#priceSummary.value = null;
                    return;
                }
                // Same pool: the server copy arrived or changed (initial pool
                // fetch, refetch after submit). Info reseats only from a
                // loaded pool — while `pool` is null the empty placeholder
                // must not clobber a baseline that seeded earlier. `peek` so
                // baseline/draft writes elsewhere don't re-run the effect.
                if (pool !== null && (lastServerInfo === null || !isSamePoolInfo(serverInfo, lastServerInfo))) {
                    lastServerInfo = serverInfo;
                    const infoWasClean = isSamePoolInfo(this.#poolInfoDraft.peek(), this.#poolInfoSaved.peek());
                    this.#poolInfoSaved.value = serverInfo;
                    if (infoWasClean) this.#poolInfoDraft.value = serverInfo;
                }
                // Only a non-null pricing copy reseats — a null one is just
                // the stale cache between a successful PATCH and its refetch,
                // and must not clobber the freshly saved config.
                if (serverPricing === null) return;
                this.#poolHasServerPricing.value = true;
                if (isSamePoolPricingConfig(serverPricing, this.#poolPricingSaved.value)) return;
                const wasClean = isSamePoolPricingConfig(this.#poolPricingDraft.value, this.#poolPricingSaved.value);
                this.#poolPricingSaved.value = serverPricing;
                if (wasClean) this.#poolPricingDraft.value = serverPricing;
            }),
        );

        // Re-simulate (debounced) whenever the simulator symbol or the draft
        // config changes, so hierarchy / minimum-price edits preview live.
        const scheduleSimulation = debounce(() => void this.#runSimulation(), SIMULATOR_DEBOUNCE_MS);
        this.#disposableStack.defer(
            effect(() => {
                // Subscribe to the run inputs; the debounced runner re-reads
                // them fresh when it fires. `selectedPoolId` is the narrow
                // computed — reading `#state` here would re-trigger on every
                // dispatch (row edits, dialogs, pagination).
                void this.#simulatorSymbol.value;
                void this.#poolPricingDraft.value;
                void this.selectedPoolId.value;
                scheduleSimulation();
            }),
        );
        this.#disposableStack.defer(() => {
            scheduleSimulation.cancel();
            // Orphan any in-flight run: settlement checks the token and
            // drops its result, so no state write or toast lands after
            // teardown.
            this.#simulatorRun++;
        });
    }

    #dispatch(action: PoolManagementAction): void {
        this.#state.value = poolManagementReducer(this.#state.value, action);
    }

    /**
     * Enqueue a persistent "in-flight" toast and return its id so the caller
     * can dismiss it once the action settles. Callers should follow up with
     * {@link #replaceToast} on both success and failure so the loader never
     * lingers after the work is done.
     */
    #startActionToast(message: string): string {
        const id = crypto.randomUUID();
        this.#store.dispatch({
            type: 'toast::enqueue',
            toast: {
                id,
                message,
                group: 'default',
                variant: 'info',
                autoHideDuration: null,
            },
        });
        return id;
    }

    #replaceToast(startId: string, finalToast: { message: string; variant: 'success' | 'error' }): void {
        this.#store.dispatch({ type: 'toast::dismiss', id: startId });
        this.#store.dispatch({
            type: 'toast::enqueue',
            toast: {
                message: finalToast.message,
                group: 'default',
                variant: finalToast.variant,
            },
        });
    }

    /**
     * Sets the firm scope for this model. Pass `null` for the superuser
     * "list all pools" mode. Resets the open pool and any in-flight
     * edits when the scope changes. (Firm display name is owned by the
     * page, not the model — see `PoolManagementPage`.)
     */
    setFirm(firmId: string | null): void {
        if (this.#firmId.value === firmId) return;
        // Unsaved edits in the current pool? Park the rest behind the
        // discard prompt; the closure below runs verbatim after confirm.
        this.#runOrPromptDiscard(() => this.#doSetFirm(firmId));
    }

    #doSetFirm(firmId: string | null): void {
        // Drop any in-flight permission-change confirmation: the captured
        // `poolId` is stale once scope changes, and reopening another pool
        // would otherwise remount the dialog and mutate the old pool.
        this.#pendingPermissionChange.value = null;
        this.#pendingUploadWizardPoolId = null;
        this.#firmId.value = firmId;
        // Drop the superuser-only firm-name map when leaving superuser
        // scope so `filteredPools` and `selectedPoolFirmName` can rely on
        // "map non-null ↔ superuser scope" without trusting the caller to
        // also dispatch `setFirmNames(null)`.
        if (firmId !== null) this.#firmNamesByFirmId.value = null;
        this.#dispatch(openPoolList());
        this.#pendingAddsByPool.value = new Map();
        // CLOSE_UPLOAD_WIZARD owns the upload-lifecycle teardown
        // (wizard, applying, error, summary) in a single reducer
        // transition — see its reducer case. OPEN_POOL_LIST above
        // already cleared the submit lifecycle (isSubmitting,
        // submitError) along with the per-pool edit slate.
        this.#dispatch(closeUploadWizard());
        // Same for the Create Pool dialog: its form targets the firm
        // scope it was opened under, so a scope change invalidates it.
        this.#dispatch(closeCreatePool());
    }

    setFirmNames(map: Readonly<Record<string, string>> | null): void {
        this.#firmNamesByFirmId.value = map;
    }

    setFirmName(name: string | null): void {
        this.#firmName.value = name;
    }

    // --- Loading / error signals ---
    readonly isLoading: ReadonlySignal<boolean> = computed(() => {
        if (this.#poolsQuery.signal.value.isLoading) return true;
        if (this.#state.value.selectedPoolId && this.#itemsQuery.signal.value.isLoading) return true;
        return false;
    });
    readonly error: ReadonlySignal<string | null> = computed(() => {
        const e = this.#poolsQuery.signal.value.error ?? this.#itemsQuery.signal.value.error;
        return e ? e.message : null;
    });

    // --- Pool list signals ---
    readonly pools: ReadonlySignal<ReadonlyArray<Pool>> = computed(() => {
        const overrides = this.#poolStatusOverrides.value;
        return this.#poolsQuery.signal.value.data?.pools.map((p) => fromApiPool(p, overrides.get(p.id))) ?? [];
    });
    readonly poolSearchQuery: ReadonlySignal<string> = computed(() => this.#state.value.poolSearchQuery);
    readonly poolStatusFilter: ReadonlySignal<PoolStatusFilter> = computed(() => this.#state.value.poolStatusFilter);
    readonly firmNamesByFirmId: ReadonlySignal<Readonly<Record<string, string>> | null> = computed(
        () => this.#firmNamesByFirmId.value,
    );
    readonly filteredPools: ReadonlySignal<ReadonlyArray<Pool>> = computed(() => {
        const query = this.#state.value.poolSearchQuery.trim().toLowerCase();
        const statusFilter = this.#state.value.poolStatusFilter;
        const all = this.pools.value;
        const firmNames = this.#firmNamesByFirmId.value;
        return all.filter((p) => {
            // Status filter is an AND gate ahead of the text search.
            if (statusFilter !== 'all' && p.status !== statusFilter) return false;
            if (!query) return true;
            if (p.name.toLowerCase().includes(query)) return true;
            if ((p.description ?? '').toLowerCase().includes(query)) return true;
            // Firm-name matching is superuser-only: the map is null in the
            // firm-scoped view (where every pool shares the same firm).
            if (firmNames !== null) {
                const firmName = firmNames[p.firmId];
                if (firmName && firmName.toLowerCase().includes(query)) return true;
            }
            return false;
        });
    });

    // --- View / selection signals ---
    readonly view: ReadonlySignal<PoolManagementView> = computed(() => this.#state.value.view);
    readonly selectedPoolId: ReadonlySignal<string | null> = computed(() => this.#state.value.selectedPoolId);
    readonly selectedPool: ReadonlySignal<Pool | null> = computed(() => {
        const id = this.#state.value.selectedPoolId;
        if (!id) return null;
        return this.pools.value.find((p) => p.id === id) ?? null;
    });
    readonly selectedPoolPermission: ReadonlySignal<PoolPermission | null> = computed(
        () => this.selectedPool.value?.permission ?? null,
    );
    readonly selectedPoolFirmName: ReadonlySignal<string | null> = computed(() => {
        const pool = this.selectedPool.value;
        if (!pool) return null;
        const map = this.#firmNamesByFirmId.value;
        if (!map) return null;
        return map[pool.firmId] ?? null;
    });
    /**
     * Ids of legacy pools per the `legacy-lab-pools` feature flag, as a
     * set for per-pool gates in list contexts (e.g. hiding the pool
     * card's Delete action, which the backend rejects for legacy pools).
     */
    readonly legacyPoolIds: ReadonlySignal<ReadonlySet<string>> = computed(() => new Set(this.#legacyPoolIds.value));
    /**
     * Legacy-pool gate per the `legacy-lab-pools` feature flag (payload:
     * legacy pool ids). Keyed by `selectedPoolId` — not `selectedPool` — so
     * the gate is available on deep links before the pool list resolves.
     */
    readonly selectedPoolIsLegacy: ReadonlySignal<boolean> = computed(() => {
        const poolId = this.#state.value.selectedPoolId;
        if (!poolId) return false;
        return this.legacyPoolIds.value.has(poolId);
    });

    // --- Permission gates ---
    readonly canRead: ReadonlySignal<boolean> = computed(() => this.selectedPoolPermission.value !== null);
    readonly canWrite: ReadonlySignal<boolean> = computed(() => {
        const p = this.selectedPoolPermission.value;
        return p === 'writer' || p === 'owner';
    });
    readonly canOwn: ReadonlySignal<boolean> = computed(() => this.selectedPoolPermission.value === 'owner');

    // --- Inventory row signals ---
    /**
     * Server-side rows plus any locally pending adds for the current pool.
     * Locally pending rows carry the {@link PENDING_ROW_PREFIX} so the
     * remove path can distinguish "drop the pending add" from "stage
     * backend removal".
     */
    readonly serverInventoryRows: ReadonlySignal<ReadonlyArray<InventoryRow>> = computed(() => {
        const id = this.#state.value.selectedPoolId;
        if (!id) return [];
        // Availability PATCHes render optimistically: overlay the requested
        // value until the mutation settles and the refetch reseats it.
        const overrides = this.#itemAvailabilityOverrides.value;
        const remote =
            this.#itemsQuery.signal.value.data?.items.map(fromApiItem).map((row) => {
                const override = overrides.get(row.id);
                return override === undefined || override === row.available ? row : { ...row, available: override };
            }) ?? [];
        const pending = this.#pendingAddsByPool.value.get(id) ?? [];
        if (pending.length === 0) return remote;
        // Pending adds sit ABOVE server rows so a freshly-added symbol
        // is visible at the top of the list without scrolling. Within
        // the pending block, `#addSymbolInternal` keeps newest-first.
        return [...pending, ...remote];
    });

    readonly rowEdits: ReadonlySignal<Readonly<Record<string, InventoryRowEdit>>> = computed(
        () => this.#state.value.rowEdits,
    );

    readonly stagedRowBaselines: ReadonlySignal<Readonly<Record<string, StagedRowBaseline>>> = computed(
        () => this.#state.value.stagedRowBaselines,
    );

    /**
     * Memoised on the reducer's `pendingRowRemovals` array reference so
     * unrelated state changes (e.g. search query updates) don't churn a
     * fresh `Set` and propagate a "changed" event to every subscriber.
     */
    readonly pendingRowRemovals: ReadonlySignal<ReadonlySet<string>> = (() => {
        let lastSource: ReadonlyArray<string> | null = null;
        let lastSet: ReadonlySet<string> = new Set();
        return computed(() => {
            const source = this.#state.value.pendingRowRemovals;
            if (source !== lastSource) {
                lastSource = source;
                lastSet = new Set(source);
            }
            return lastSet;
        });
    })();

    readonly displayedInventoryRows: ReadonlySignal<ReadonlyArray<InventoryRow>> = computed(() => {
        const rows = this.serverInventoryRows.value;
        const edits = this.#state.value.rowEdits;
        if (Object.keys(edits).length === 0) return rows;
        return rows.map((row) => {
            const edit = edits[row.id];
            if (!edit) return row;
            return {
                ...row,
                currentQuantity: edit.currentQuantity ?? row.currentQuantity,
                currentLabPrice: edit.currentLabPrice ?? row.currentLabPrice,
            };
        });
    });

    /**
     * Pending draft rows for the selected pool with field-edits
     * applied. The Permissions / inventory table renders these in a
     * separate section above the paginated server rows so they stay
     * visible regardless of the current page, filter, or page size.
     * They never appear in {@link paginatedInventoryRows} or
     * {@link filteredInventoryRows} and they don't count toward
     * {@link inventoryTotalCount}; callers that need the union
     * (submit review, CSV download) use {@link displayedInventoryRows}.
     */
    readonly pendingDraftRows: ReadonlySignal<ReadonlyArray<InventoryRow>> = computed(() => {
        const id = this.#state.value.selectedPoolId;
        if (!id) return [];
        const pending = this.#pendingAddsByPool.value.get(id);
        if (!pending || pending.length === 0) return [];
        const edits = this.#state.value.rowEdits;
        if (Object.keys(edits).length === 0) return pending;
        return pending.map((row) => {
            const edit = edits[row.id];
            if (!edit) return row;
            return {
                ...row,
                currentQuantity: edit.currentQuantity ?? row.currentQuantity,
                currentLabPrice: edit.currentLabPrice ?? row.currentLabPrice,
            };
        });
    });

    readonly filteredInventoryRows: ReadonlySignal<ReadonlyArray<InventoryRow>> = computed(() => {
        // Server rows for the current view, excluding drafts (which
        // render in their own always-visible section above the table).
        // When a symbol search is active the backend has already
        // narrowed these to the matching items, so there is no
        // client-side filtering left to do here.
        return this.displayedInventoryRows.value.filter((r) => !isDraftRowId(r.id));
    });

    readonly totalDailyRevenue: ReadonlySignal<number | null> = computed(() => {
        const rows = this.serverInventoryRows.value;
        let sum = 0;
        let any = false;
        for (const r of rows) {
            if (r.dailyRevenue !== null) {
                sum += r.dailyRevenue;
                any = true;
            }
        }
        // Return null when no row exposed a value so the toolbar can
        // render an em-dash placeholder instead of "$0".
        return any ? sum : null;
    });

    // --- Pagination ---
    readonly inventoryRowsPerPage: ReadonlySignal<InventoryRowsPerPage> = computed(
        () => this.#state.value.inventoryRowsPerPage,
    );
    /**
     * Total number of server-side items in the pool. Backed by the
     * `count` field added in locates-ws #449. Pending draft adds are
     * deliberately excluded — they render in their own always-visible
     * section above the paginated table and don't participate in the
     * "showing X-Y of N" label. Returns `0` while the query hasn't
     * reported a count yet.
     */
    readonly inventoryTotalCount: ReadonlySignal<number> = computed(() => {
        const id = this.#state.value.selectedPoolId;
        if (!id) return 0;
        return this.#itemsQuery.signal.value.data?.count ?? 0;
    });
    readonly inventoryPageCount: ReadonlySignal<number> = computed(() => {
        const perPage = this.#state.value.inventoryRowsPerPage;
        if (perPage === 'all') return 1;
        // `inventoryTotalCount` is the right total in both modes: a paged
        // fetch reports the full pool size, and a symbol search reports
        // the match count (the backend sets `count === items.length`).
        return Math.max(1, Math.ceil(this.inventoryTotalCount.value / perPage));
    });
    /**
     * Clamped page index. The stored `inventoryPage` is user-driven and
     * could be left out-of-range after the filter narrows; this view
     * snaps it back into `[1, pageCount]` so consumers don't have to.
     */
    readonly inventoryPage: ReadonlySignal<number> = computed(() => {
        const requested = this.#state.value.inventoryPage;
        const max = this.inventoryPageCount.value;
        if (!Number.isFinite(requested) || requested < 1) return 1;
        if (requested > max) return max;
        return Math.floor(requested);
    });
    readonly paginatedInventoryRows: ReadonlySignal<ReadonlyArray<InventoryRow>> = computed(() => {
        const s = this.#state.value;
        const filtering = s.appliedSymbolSearch.trim() !== '';
        const perPage = s.inventoryRowsPerPage;
        // Filter-active and "all" both bypass server paging (the symbol
        // search returns every match), so the slice is purely client-side.
        // Drafts are excluded here — they render in their own section
        // above the table and never participate in filter / page math.
        if (filtering || perPage === 'all') {
            const rows = this.filteredInventoryRows.value;
            if (perPage === 'all' || !filtering) return rows;
            // Filtering with a numeric page size: still slice client-side
            // so the user can page through search results.
            const page = this.inventoryPage.value;
            const start = (page - 1) * perPage;
            return rows.slice(start, start + perPage);
        }
        // Paged server fetch: the query already holds the right page;
        // we just need to strip any draft rows that snuck in via
        // `displayedInventoryRows` (drafts render in their own section
        // above the table — see {@link pendingDraftRows}).
        return this.displayedInventoryRows.value.filter((r) => !isDraftRowId(r.id));
    });
    setInventoryPage(page: number): void {
        this.#dispatch(setInventoryPage(page));
    }
    setInventoryRowsPerPage(rowsPerPage: InventoryRowsPerPage): void {
        this.#dispatch(setInventoryRowsPerPage(rowsPerPage));
    }

    // --- Detail-view editing signals ---
    readonly symbolSearchQuery: ReadonlySignal<string> = computed(() => this.#state.value.symbolSearchQuery);
    readonly addSymbolDraft: ReadonlySignal<string> = computed(() => this.#state.value.addSymbolDraft);
    readonly isDirty: ReadonlySignal<boolean> = computed(() => {
        const s = this.#state.value;
        if (Object.keys(s.rowEdits).length > 0) return true;
        if (s.pendingRowRemovals.length > 0) return true;
        // A typed-but-not-yet-added symbol counts as unsaved work: any
        // navigation/reset guard that consults `isDirty` should prompt the
        // user before discarding it.
        if (s.addSymbolDraft.trim() !== '') return true;
        const poolId = s.selectedPoolId;
        if (poolId && (this.#pendingAddsByPool.value.get(poolId)?.length ?? 0) > 0) return true;
        return false;
    });
    readonly unsavedChangeCount: ReadonlySignal<number> = computed(() => {
        const s = this.#state.value;
        const poolId = s.selectedPoolId;
        const additions = poolId ? (this.#pendingAddsByPool.value.get(poolId)?.length ?? 0) : 0;
        const removals = new Set(s.pendingRowRemovals);
        // Edits on draft rows are part of constructing the addition; edits
        // on rows already staged for removal will be discarded on submit.
        const edits = Object.keys(s.rowEdits).filter((id) => !isDraftRowId(id) && !removals.has(id)).length;
        return additions + edits + removals.size;
    });
    readonly submitReviewOpen: ReadonlySignal<boolean> = computed(() => this.#state.value.submitReviewOpen);

    // --- Submit lifecycle (driven entirely by reducer state) ---
    readonly isSubmitting: ReadonlySignal<boolean> = computed(() => this.#state.value.isSubmitting);
    readonly isDownloadingCsv: ReadonlySignal<boolean> = computed(() => this.#state.value.isDownloadingCsv);
    readonly submitError: ReadonlySignal<string | null> = computed(() => this.#state.value.submitError);

    // --- Navigation commands ---
    openPoolList(): void {
        this.#runOrPromptDiscard(() => {
            this.#pendingPermissionChange.value = null;
            this.#pendingUploadWizardPoolId = null;
            this.#dispatch(openPoolList());
        });
    }
    openPoolDetail(poolId: string): void {
        // A click on the already-open pool would otherwise burn an
        // unnecessary discard prompt; short-circuit when the target is
        // the current pool.
        if (this.#state.value.selectedPoolId === poolId && this.#state.value.view === 'detail') return;
        this.#runOrPromptDiscard(() => {
            this.#pendingPermissionChange.value = null;
            this.#dispatch(openPoolDetail(poolId));
            // Consume the create-with-upload intent: OPEN_POOL_DETAIL just
            // scrubbed any stale wizard state, so this open always yields a
            // fresh 'drop'-step wizard. A mismatched pool id drops the
            // intent instead — the user navigated somewhere else first.
            const pendingUploadFor = this.#pendingUploadWizardPoolId;
            this.#pendingUploadWizardPoolId = null;
            if (pendingUploadFor === poolId) this.#dispatch(openUploadWizard());
            // The pool-detail query observers refetch when `poolId`
            // changes, so the view is fresh as of right now — reset
            // the stamp so the toolbar doesn't read "Last refresh: 5
            // minutes ago" against data we just loaded.
            this.#markFresh();
        });
    }

    // --- Unsaved-changes guard ---
    readonly discardChangesOpen: ReadonlySignal<boolean> = computed(() => this.#pendingDiscard.value !== null);

    /**
     * Run `proceed` immediately when there are no unsaved edits.
     * Otherwise stash it behind the discard prompt: `confirmDiscardChanges`
     * runs it after wiping edits, `cancelDiscardChanges` runs `onCancel`
     * (if provided) so the caller can react to the user keeping their
     * work. Used internally by the navigation methods and externally by
     * the React shim's route blocker.
     */
    requestDiscardChanges(proceed: () => void, onCancel?: () => void): void {
        this.#runOrPromptDiscard(proceed, onCancel);
    }

    #runOrPromptDiscard(proceed: () => void, onCancel?: () => void): void {
        if (this.hasUnsavedChanges.value) {
            // Re-entrancy: a second navigation request can arrive while
            // a prompt is already open (e.g. the router blocker fires
            // again on a popstate). Fire the existing `onCancel` first
            // so any external blocker waiting on it (e.g. `blocker.reset`)
            // gets resolved before its callback is replaced.
            const previousCancel = this.#pendingDiscardCancel.value;
            this.#pendingDiscard.value = proceed;
            this.#pendingDiscardCancel.value = onCancel ?? null;
            previousCancel?.();
            return;
        }
        proceed();
    }

    confirmDiscardChanges(): void {
        const proceed = this.#pendingDiscard.value;
        if (!proceed) return;
        this.#pendingDiscard.value = null;
        // Confirm wins over cancel — drop the cancel callback so a
        // later `cancelDiscardChanges` (e.g. a delayed `blocker.reset`)
        // can't double-fire it.
        this.#pendingDiscardCancel.value = null;
        // Capture the current pool id BEFORE invoking `proceed`: the
        // callback may navigate to another pool (or back to the list),
        // which would change `selectedPoolId` before we clear the
        // pending-adds map. Drop pending adds for the pool we're
        // leaving. The reducer's OPEN_POOL_LIST / OPEN_POOL_DETAIL
        // transitions clear rowEdits, pendingRowRemovals,
        // addSymbolDraft, and submit state already; firm switches handle
        // pendingAdds inside `#doSetFirm`.
        const currentPoolId = this.#state.value.selectedPoolId;
        if (currentPoolId) {
            const next = new Map(this.#pendingAddsByPool.value);
            if (next.delete(currentPoolId)) this.#pendingAddsByPool.value = next;
        }
        proceed();
    }

    cancelDiscardChanges(): void {
        if (this.#pendingDiscard.value === null) return;
        const onCancel = this.#pendingDiscardCancel.value;
        this.#pendingDiscard.value = null;
        this.#pendingDiscardCancel.value = null;
        onCancel?.();
    }

    // --- Pool detail tabs ---
    /**
     * Session-level access gate for the Permissions tab and its
     * mutations. Derived directly from `CurrentUserModel` so the model
     * picks up superuser toggles automatically without a separate
     * React-side `setSuperuserEnabled` round-trip. `isSuperuser` is the
     * static "this user is allowed to be a superuser" flag;
     * `isSuperUserModeEnabled` is the time-bounded session toggle the
     * admin saga maintains (it clears when the access window expires).
     */
    readonly canManagePermissions: ReadonlySignal<boolean> = computed(
        () => this.#currentUser.isSuperuser.value && this.#currentUser.isSuperUserModeEnabled.value,
    );

    // --- Pricing resolution sidecard (PLAT-12936) ---
    /**
     * Gate for the read-only pricing views (the row sidecard and the Info
     * tab's pricing cards). The backing item-pricing endpoint requires
     * `reader` on the pool — the same access that shows the inventory
     * table — so everyone who can see a row may see how it prices. Not
     * legacy-gated: legacy pools are gaining pricing support too.
     */
    readonly canViewPricingResolution: ReadonlySignal<boolean> = computed(() => this.canRead.value);

    /**
     * Gate for editing the pool's pricing config: the pool PATCH requires
     * `owner`, which is also what firm admins hold on their firm's pools.
     */
    readonly canEditPricingConfig: ReadonlySignal<boolean> = computed(() => this.canOwn.value);

    readonly #pricingResolutionCard = signal<PricingResolutionCardState | null>(null);
    readonly pricingResolutionCard: ReadonlySignal<PricingResolutionCardState | null> = computed(
        () => this.#pricingResolutionCard.value,
    );

    /**
     * Monotonic show-token: settlement handlers capture it and drop their
     * result when a newer show has started since, so a stale response (the
     * row's price was edited between hovers, changing the request) can
     * never overwrite the newer card.
     */
    #pricingResolutionShow = 0;

    showPricingResolution(rowId: string): void {
        if (!this.canViewPricingResolution.value) return;
        const poolId = this.#state.value.selectedPoolId;
        if (!poolId) return;
        // Draft rows have no server-side item to price yet.
        const row = this.displayedInventoryRows.value.find((r) => r.id === rowId);
        if (!row || isDraftRowId(row.id)) return;
        const show = ++this.#pricingResolutionShow;

        const query = getPoolItemPricingQuery(poolId, rowId);
        // Seed from the cache so a re-hover opens `ready` without a loader
        // flash; `fetchQuery` still revalidates when the entry is stale and
        // the settle path below reconciles the state.
        const cached = this.#queryClient.getQueryData<PoolPricingSimulationResponse>(query.queryKey);
        this.#pricingResolutionCard.value = cached
            ? { rowId, status: 'ready', resolution: derivePricingResolution(cached) }
            : { rowId, status: 'loading' };
        void this.#queryClient.fetchQuery(query).then(
            (response) => {
                if (show !== this.#pricingResolutionShow) return;
                if (this.#pricingResolutionCard.value?.rowId !== rowId) return;
                this.#pricingResolutionCard.value = {
                    rowId,
                    status: 'ready',
                    resolution: derivePricingResolution(response),
                };
            },
            (error: unknown) => {
                if (show !== this.#pricingResolutionShow) return;
                const current = this.#pricingResolutionCard.value;
                if (current?.rowId !== rowId) return;
                // A failed revalidation must not clobber a usable cached
                // view: keep the last-known-good resolution and stay quiet.
                if (current.status === 'ready') return;
                // 400 is the backend's "pool has no pricing configured" —
                // an expected state for unconfigured pools, not a failure.
                if (isAxiosError(error) && error.response?.status === 400) {
                    this.#pricingResolutionCard.value = { rowId, status: 'not-configured' };
                    return;
                }
                this.#pricingResolutionCard.value = { rowId, status: 'error' };
                this.#pricingErrorToast('admin::pool-management::toast-pricing-resolution-failed', row.symbol, error);
            },
        );
    }

    /**
     * Error toast for the pricing fetch paths — including the 403s firm
     * admins/owners see while the endpoint-permission rollout is in flight.
     */
    #pricingErrorToast(
        key:
            | 'admin::pool-management::toast-pricing-resolution-failed'
            | 'admin::pool-management::toast-simulator-failed'
            | 'admin::pool-management::toast-price-summary-failed',
        symbol: string,
        error: unknown,
    ): void {
        this.#store.dispatch({
            type: 'toast::enqueue',
            toast: {
                message: t(key, { symbol, message: error instanceof Error ? error.message : String(error) }),
                group: 'default',
                variant: 'error',
            },
        });
    }

    hidePricingResolution(rowId: string): void {
        // Guarded per row: when hovering quickly from row A to row B, A's
        // teardown can run after B's show — only A's own state may clear.
        if (this.#pricingResolutionCard.value?.rowId !== rowId) return;
        this.#pricingResolutionCard.value = null;
    }

    // --- Pool pricing configuration (PLAT-12937) ---
    // The baseline seeds from the pool payload's persisted `pricing` (or
    // the default config while the pool has none); submit PATCHes the pool.
    readonly #poolPricingSaved = signal<PoolPricingConfig>(DEFAULT_POOL_PRICING_CONFIG);
    readonly #poolPricingDraft = signal<PoolPricingConfig>(DEFAULT_POOL_PRICING_CONFIG);
    /** Whether the open pool has a pricing config persisted server-side. */
    readonly #poolHasServerPricing = signal(false);
    readonly #pricingConfirmOpen = signal(false);
    readonly #isSavingPricing = signal(false);

    readonly poolPricingSavedConfig: ReadonlySignal<PoolPricingConfig> = computed(() => this.#poolPricingSaved.value);
    readonly poolPricingDraftConfig: ReadonlySignal<PoolPricingConfig> = computed(() => this.#poolPricingDraft.value);
    readonly isPricingConfigDirty: ReadonlySignal<boolean> = computed(
        () => !isSamePoolPricingConfig(this.#poolPricingDraft.value, this.#poolPricingSaved.value),
    );

    /**
     * Anything the discard prompts must protect: staged row edits, an
     * unsaved pricing config draft, or an unsaved pool info draft. The
     * pool-switch effect resets both drafts, so a confirmed navigation
     * discards them implicitly.
     */
    readonly hasUnsavedChanges: ReadonlySignal<boolean> = computed(
        () => this.isDirty.value || this.isPricingConfigDirty.value || this.isPoolInfoDirty.value,
    );
    readonly isPricingConfirmOpen: ReadonlySignal<boolean> = computed(() => this.#pricingConfirmOpen.value);

    readonly isSavingPricingConfig: ReadonlySignal<boolean> = computed(() => this.#isSavingPricing.value);

    setPricingHierarchy(hierarchy: PoolPricingMethod[]): void {
        if (!this.canEditPricingConfig.value) return;
        const current = this.#poolPricingDraft.value.hierarchy;
        // Reject anything that isn't a permutation of the current methods —
        // drag/drop handlers can only reorder, never add or drop a method.
        const isPermutation =
            hierarchy.length === current.length && current.every((method) => hierarchy.includes(method));
        if (!isPermutation) return;
        this.#poolPricingDraft.value = { ...this.#poolPricingDraft.value, hierarchy: [...hierarchy] };
    }

    setMinimumPriceEnabled(enabled: boolean): void {
        if (!this.canEditPricingConfig.value) return;
        const draft = this.#poolPricingDraft.value;
        // Preserve the persisted-but-disabled floors (backend contract).
        this.#poolPricingDraft.value = {
            ...draft,
            minimumPrice: { enabled, prices: draft.minimumPrice?.prices ?? {} },
        };
    }

    /**
     * Set (or clear, with `undefined`) the draft's price floor for one
     * borrow status. A status without a floor is unfloored.
     */
    setMinimumPriceForStatus(status: BorrowStatusKey, price: number | undefined): void {
        if (!this.canEditPricingConfig.value) return;
        const draft = this.#poolPricingDraft.value;
        if (price !== undefined && (!Number.isFinite(price) || price < 0)) return;
        const prices = { ...draft.minimumPrice?.prices };
        if (price === undefined) {
            delete prices[status];
        } else {
            prices[status] = price;
        }
        this.#poolPricingDraft.value = {
            ...draft,
            minimumPrice: { enabled: draft.minimumPrice?.enabled ?? false, prices },
        };
    }

    discardPricingConfigChanges(): void {
        if (!this.canEditPricingConfig.value) return;
        this.#poolPricingDraft.value = this.#poolPricingSaved.value;
    }

    requestSubmitPricingConfig(): void {
        if (!this.canEditPricingConfig.value) return;
        if (!this.isPricingConfigDirty.value) return;
        this.#pricingConfirmOpen.value = true;
    }

    cancelSubmitPricingConfig(): void {
        this.#pricingConfirmOpen.value = false;
    }

    confirmSubmitPricingConfig(): void {
        if (!this.canEditPricingConfig.value) return;
        if (this.#isSavingPricing.value) return;
        const poolId = this.#state.value.selectedPoolId;
        if (!poolId) return;
        const pricing = this.#poolPricingDraft.value;
        this.#isSavingPricing.value = true;
        void updatePool({ poolId, request: { pricing } }).then(
            async (response) => {
                this.#isSavingPricing.value = false;
                this.#pricingConfirmOpen.value = false;
                // The echo only applies while the same pool is still open —
                // a fast pool switch must not resurrect the previous pool's
                // config.
                if (this.#state.value.selectedPoolId === poolId) {
                    // Reconcile against the server's echo rather than the draft —
                    // but never clobber edits the admin made while the PATCH was
                    // in flight; those stay dirty against the new baseline.
                    const saved = response.pool.pricing ?? pricing;
                    const untouched = isSamePoolPricingConfig(this.#poolPricingDraft.value, pricing);
                    this.#poolPricingSaved.value = saved;
                    if (untouched) this.#poolPricingDraft.value = saved;
                    this.#poolHasServerPricing.value = true;
                }
                this.#store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: t('admin::pool-management::toast-pricing-saved'),
                        group: 'default',
                        variant: 'success',
                    },
                });
                try {
                    await invalidatePoolsQuery(this.#queryClient, this.#firmId.value ?? undefined);
                    this.#markFresh();
                } catch {
                    // Best-effort cache refresh — a failed invalidation just
                    // leaves the stale pool entry until the next fetch.
                }
            },
            (error: unknown) => {
                this.#isSavingPricing.value = false;
                const message = error instanceof Error ? error.message : String(error);
                this.#store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: t('admin::pool-management::toast-pricing-save-failed', { message }),
                        group: 'default',
                        variant: 'error',
                    },
                });
            },
        );
    }

    // --- Pool info editing (PLAT-12970) ---
    /**
     * The pool PATCH requires `owner`. Deliberately not legacy-gated:
     * legacy pools are gaining PATCH support (name/description/email),
     * and until it lands a failed PATCH surfaces as an error toast.
     */
    readonly canEditPoolInfo: ReadonlySignal<boolean> = computed(() => this.canOwn.value);

    readonly #poolInfoSaved = signal<PoolInfoDraft>(EMPTY_POOL_INFO);
    readonly #poolInfoDraft = signal<PoolInfoDraft>(EMPTY_POOL_INFO);
    readonly #isSavingPoolInfo = signal(false);

    readonly poolInfoSaved: ReadonlySignal<PoolInfoDraft> = computed(() => this.#poolInfoSaved.value);
    readonly poolInfoDraft: ReadonlySignal<PoolInfoDraft> = computed(() => this.#poolInfoDraft.value);
    readonly isPoolInfoDirty: ReadonlySignal<boolean> = computed(
        () => !isSamePoolInfo(this.#poolInfoDraft.value, this.#poolInfoSaved.value),
    );
    readonly isPoolInfoDraftValid: ReadonlySignal<boolean> = computed(() => {
        const draft = this.#poolInfoDraft.value;
        return draft.name.trim() !== '' && EVENT_EMAIL_PATTERN.test(draft.eventEmail.trim());
    });
    readonly isSavingPoolInfo: ReadonlySignal<boolean> = computed(() => this.#isSavingPoolInfo.value);

    setPoolInfoField(field: keyof PoolInfoDraft, value: string): void {
        if (!this.canEditPoolInfo.value) return;
        this.#poolInfoDraft.value = { ...this.#poolInfoDraft.value, [field]: value };
    }

    discardPoolInfoChanges(): void {
        if (!this.canEditPoolInfo.value) return;
        // A discard during an in-flight PATCH would be indistinguishable
        // from a mid-flight edit at settle time, resurrecting the old
        // value as dirty against the new baseline — so it waits.
        if (this.#isSavingPoolInfo.value) return;
        this.#poolInfoDraft.value = this.#poolInfoSaved.value;
    }

    submitPoolInfo(): void {
        if (!this.canEditPoolInfo.value) return;
        if (this.#isSavingPoolInfo.value) return;
        if (!this.isPoolInfoDirty.value || !this.isPoolInfoDraftValid.value) return;
        const poolId = this.#state.value.selectedPoolId;
        if (!poolId) return;
        const saved = this.#poolInfoSaved.value;
        const submitted = this.#poolInfoDraft.value;
        // Send only the fields that changed; the backend leaves absent
        // fields untouched.
        const request = {
            ...(submitted.name !== saved.name ? { name: submitted.name.trim() } : {}),
            ...(submitted.description !== saved.description ? { description: submitted.description.trim() } : {}),
            ...(submitted.eventEmail !== saved.eventEmail ? { eventEmail: submitted.eventEmail.trim() } : {}),
        } satisfies UpdatePoolContractRequest;
        this.#isSavingPoolInfo.value = true;
        void updatePool({ poolId, request }).then(
            async (response) => {
                this.#isSavingPoolInfo.value = false;
                // The echo only applies while the same pool is still open —
                // a fast pool switch must not resurrect the previous pool's
                // fields.
                if (this.#state.value.selectedPoolId === poolId) {
                    const echoed: PoolInfoDraft = {
                        name: response.pool.name,
                        description: response.pool.description ?? '',
                        eventEmail: response.pool.eventEmail,
                    };
                    // Reconcile against the server's echo (it trims), but
                    // never clobber edits made while the PATCH was in
                    // flight; those stay dirty against the new baseline.
                    const untouched = isSamePoolInfo(this.#poolInfoDraft.value, submitted);
                    this.#poolInfoSaved.value = echoed;
                    if (untouched) this.#poolInfoDraft.value = echoed;
                }
                this.#store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: t('admin::pool-management::toast-info-saved'),
                        group: 'default',
                        variant: 'success',
                    },
                });
                try {
                    await invalidatePoolsQuery(this.#queryClient, this.#firmId.value ?? undefined);
                    this.#markFresh();
                } catch {
                    // Best-effort cache refresh — a failed invalidation just
                    // leaves the stale pool entry until the next fetch.
                }
            },
            (error: unknown) => {
                this.#isSavingPoolInfo.value = false;
                const message = error instanceof Error ? error.message : String(error);
                this.#store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: t('admin::pool-management::toast-info-save-failed', { message }),
                        group: 'default',
                        variant: 'error',
                    },
                });
            },
        );
    }

    // --- Price summary dialog (PLAT-12938) ---
    /**
     * Gate for the price summary: its backing endpoint
     * (`GET /v1/orders/prices/{symbol}`) stays behind the backend's
     * price-index capability — superuser or a dedicated access role the UI
     * cannot see — so only superuser sessions get the trigger; pool
     * ownership alone does not grant the data.
     */
    readonly canViewPriceSummary: ReadonlySignal<boolean> = computed(
        () => this.#currentUser.isSuperuser.value && this.#currentUser.isSuperUserModeEnabled.value,
    );

    readonly #priceSummary = signal<PriceSummaryState | null>(null);
    readonly priceSummary: ReadonlySignal<PriceSummaryState | null> = computed(() => this.#priceSummary.value);
    /**
     * Monotonic open-token: settlement handlers capture it and drop their
     * result when a newer open (or a close) has happened since. The symbol
     * guard alone cannot tell two opens of the SAME symbol apart — e.g. a
     * lagging providers lookup from another firm's pool must not stamp
     * that firm's names onto a reopened dialog.
     */
    #priceSummaryOpen = 0;

    openPriceSummary(rowId: string): void {
        if (!this.canViewPriceSummary.value) return;
        const row =
            this.displayedInventoryRows.value.find((r) => r.id === rowId) ??
            this.pendingDraftRows.value.find((r) => r.id === rowId);
        if (!row) return;
        const symbol = row.symbol;
        const open = ++this.#priceSummaryOpen;

        // The index identifies providers by id alone; the firm's provider
        // list resolves them to names. Best-effort — a caller who cannot
        // list providers just sees the ids, which is the backend's intent.
        const firmId = this.selectedPool.value?.firmId;
        const providersQuery = getAllProvidersQuery(firmId);
        const providerNamesFrom = (providers: readonly Provider[] | undefined): Readonly<Record<string, string>> =>
            Object.fromEntries((providers ?? []).map((provider) => [provider.id, provider.name]));

        const query = getSymbolPricesQuery(symbol);
        // Seed from the cache so a re-open shows the levels immediately;
        // `fetchQuery` still revalidates when the entry is stale. The
        // dialog never waits on the (best-effort) providers lookup —
        // prices show as soon as they load and names patch in after.
        const cached = this.#queryClient.getQueryData<SymbolPricesResponse>(query.queryKey);
        let latestNames = providerNamesFrom(this.#queryClient.getQueryData<Provider[]>(providersQuery.queryKey));
        this.#priceSummary.value = cached
            ? { symbol, status: 'ready', prices: cached, providerNames: latestNames }
            : { symbol, status: 'loading' };
        void this.#queryClient
            .fetchQuery(providersQuery)
            .catch(() => undefined)
            .then((providers) => {
                if (open !== this.#priceSummaryOpen) return;
                if (providers === undefined) return;
                latestNames = providerNamesFrom(providers);
                const current = this.#priceSummary.value;
                if (current?.symbol !== symbol || current.status !== 'ready') return;
                this.#priceSummary.value = { ...current, providerNames: latestNames };
            });
        void this.#queryClient.fetchQuery(query).then(
            (prices) => {
                if (open !== this.#priceSummaryOpen) return;
                if (this.#priceSummary.value?.symbol !== symbol) return;
                this.#priceSummary.value = { symbol, status: 'ready', prices, providerNames: latestNames };
            },
            (error: unknown) => {
                if (open !== this.#priceSummaryOpen) return;
                const current = this.#priceSummary.value;
                if (current?.symbol !== symbol) return;
                // A failed revalidation must not clobber usable cached
                // levels: keep the last-known-good view and stay quiet.
                if (current.status === 'ready') return;
                this.#priceSummary.value = { symbol, status: 'error' };
                this.#pricingErrorToast('admin::pool-management::toast-price-summary-failed', symbol, error);
            },
        );
    }

    closePriceSummary(): void {
        // Orphan in-flight settlements so a reopened dialog cannot receive
        // a previous open's results.
        this.#priceSummaryOpen++;
        this.#priceSummary.value = null;
    }

    // --- Pricing simulator (PLAT-12937) ---
    readonly #simulatorSymbol = signal('');
    readonly #simulatorState = signal<PricingSimulatorState | null>(null);
    /**
     * Monotonic run token. Runs snapshot it on start and drop their result
     * when a newer run has started since — the debounced trigger plus the
     * query-cache dedupe make overlaps rare, but symbol edits mid-flight
     * must never let a stale response overwrite a newer one.
     */
    #simulatorRun = 0;

    readonly simulatorSymbol: ReadonlySignal<string> = computed(() => this.#simulatorSymbol.value);
    readonly simulatorState: ReadonlySignal<PricingSimulatorState | null> = computed(() => this.#simulatorState.value);

    setSimulatorSymbol(value: string): void {
        this.#simulatorSymbol.value = value;
    }

    async #runSimulation(): Promise<void> {
        const run = ++this.#simulatorRun;
        const poolId = this.#state.value.selectedPoolId;
        const symbol = this.#simulatorSymbol.value.trim().toUpperCase();
        if (!poolId || !symbol || !this.canViewPricingResolution.value) {
            this.#simulatorState.value = null;
            return;
        }
        const config = this.#poolPricingDraft.value;
        const isCleanDraft = isSamePoolPricingConfig(config, this.#poolPricingSaved.value);
        // A pool with no persisted config has no reader-scoped source for
        // the candidate prices — only the superuser-only arbitrary-config
        // simulation can preview it. Everyone else gets a hint instead of
        // a request that can only 403 on every re-run.
        const hasSuperuserSession =
            this.#currentUser.isSuperuser.value && this.#currentUser.isSuperUserModeEnabled.value;
        if (!this.#poolHasServerPricing.value && !hasSuperuserSession) {
            this.#simulatorState.value = { symbol, status: 'requires-saved-config' };
            return;
        }
        // Keep a previous result for the same symbol on screen while
        // revalidating (config tweaks), only flash the loader on a new symbol.
        if (this.#simulatorState.value?.symbol !== symbol) {
            this.#simulatorState.value = { symbol, status: 'loading' };
        }
        try {
            // The admin candidate comes from the pool's own inventory record
            // for the symbol, when it has one; the backend resolves the
            // borrow status itself and echoes it on the response.
            const items = await this.#queryClient.fetchQuery(getPoolItemsQuery(poolId, { symbol }));
            const item = items.items.find((candidate) => candidate.symbol === symbol);
            // A pool with a persisted config previews via its reader-scoped
            // endpoint; only unconfigured pools (superuser sessions, per the
            // gate above) go through the arbitrary-config simulation, which
            // simulates the draft directly.
            const usesPoolEndpoint = this.#poolHasServerPricing.value;
            const query = usesPoolEndpoint
                ? // An absent stored price is an absent `admin` candidate,
                  // not a zero one — omit rather than default.
                  getPoolPricingSimulationQuery(poolId, { symbol, adminPrice: item?.price })
                : getPricingSimulationQuery({ symbol, adminPrice: item?.price ?? 0, config });
            const serverResponse = await this.#queryClient.fetchQuery(query);
            if (run !== this.#simulatorRun) return;
            // On the pool endpoint, unsaved edits preview by re-resolving
            // the server's market inputs against the draft locally — the
            // arbitrary-config endpoint that could do it server-side is
            // superuser-only.
            const response =
                usesPoolEndpoint && !isCleanDraft ? previewDraftSimulation(config, serverResponse) : serverResponse;
            this.#simulatorState.value = {
                symbol,
                status: 'ready',
                resolution: derivePricingResolution(response),
                borrowStatus: response.borrowStatus,
            };
        } catch (error: unknown) {
            if (run !== this.#simulatorRun) return;
            // Only toast on the transition into the error state — the
            // debounced re-runs (every config tweak or keystroke) would
            // otherwise stack a toast per failed attempt.
            const wasError = this.#simulatorState.value?.status === 'error';
            this.#simulatorState.value = { symbol, status: 'error' };
            if (!wasError) {
                this.#pricingErrorToast('admin::pool-management::toast-simulator-failed', symbol, error);
            }
        }
    }

    readonly lastRefreshAt: ReadonlySignal<number> = computed(() => this.#lastRefreshAt.value);

    /**
     * Bump the refresh stamp to `now()`. Called from every successful
     * mutation path after its query invalidations settle, so the
     * toolbar's "Last refresh" label always reflects the moment the
     * pool view actually became fresh (manual refresh, submit, upload,
     * pool status toggle, permissions update).
     */
    #markFresh(): void {
        this.#lastRefreshAt.value = Date.now();
    }

    readonly activePoolDetailTab: ReadonlySignal<PoolDetailTab> = computed(() => {
        const stored = this.#state.value.activePoolDetailTab;
        // Non-superusers can't see the Permissions tab — coerce a stored
        // `permissions` value on read so they land on the Info tab.
        if (stored === 'permissions' && !this.canManagePermissions.value) return 'info';
        return stored;
    });
    setActivePoolDetailTab(tab: PoolDetailTab): void {
        // Hard-stop attempts to land on Permissions when the user can't
        // see it; this prevents the view from briefly opening the tab on
        // a stale dispatch.
        if (tab === 'permissions' && !this.canManagePermissions.value) return;
        this.#dispatch(setActivePoolDetailTab(tab));
    }

    // --- Change Log tab ---
    /**
     * `true` while the change-log query for the open pool is fetching
     * its first page (no cached data yet). Lets the view show a loading
     * placeholder instead of the empty-state CTA during the gap between
     * activating the tab and the response arriving.
     */
    readonly changeLogLoading: ReadonlySignal<boolean> = computed(() => {
        const q = this.#changeLogQuery.signal.value;
        return q.data === undefined && q.isLoading === true;
    });

    readonly changeLogEntries: ReadonlySignal<ReadonlyArray<InventoryChangeEntry>> = computed(() => {
        const data = this.#changeLogQuery.signal.value.data;
        if (!data) return [];
        // Newest-first; backend ordering isn't part of the contract so we sort
        // here to keep the timeline / day grouping stable. Symbol / action /
        // time-range filtering is delegated to the backend via the change-log
        // query's `$filter` param (see {@link buildChangeLogFilter}), so it
        // operates over the whole log and stays consistent with pagination —
        // we deliberately do not re-filter the fetched page here.
        return data.entries.map(fromApiChangeLogEntry).sort((a, b) => b.timestamp - a.timestamp);
    });
    readonly changeLogSymbolFilter: ReadonlySignal<string> = computed(() => this.#state.value.changeLogSymbolFilter);
    /**
     * Action filter options for the open pool: the full change-kind
     * enumeration for modern pools; just `'all'` for legacy pools, whose
     * journal can't distinguish kinds — the view hides the dropdown when
     * there is nothing to choose (PLAT-12869).
     */
    readonly changeLogActionFilterValues: ReadonlySignal<readonly ChangeLogActionFilter[]> = computed(() =>
        this.selectedPoolIsLegacy.value
            ? LEGACY_CHANGE_LOG_ACTION_FILTER_VALUES
            : MODERN_CHANGE_LOG_ACTION_FILTER_VALUES,
    );
    /**
     * Active action filter after per-pool coercion: a stored kind the open
     * pool's option set doesn't offer (e.g. a modern-only kind carried over
     * to a legacy pool) reads as 'all', so the dropdown and the `$filter`
     * sent to the backend never disagree with the offered options.
     */
    readonly changeLogActionFilter: ReadonlySignal<ChangeLogActionFilter> = computed(() => {
        const stored = this.#state.value.changeLogActionFilter;
        return this.changeLogActionFilterValues.value.includes(stored) ? stored : 'all';
    });
    readonly changeLogTimeRange: ReadonlySignal<ChangeLogTimeRange> = computed(
        () => this.#state.value.changeLogTimeRange,
    );
    setChangeLogSymbolFilter(query: string): void {
        this.#dispatch(setChangeLogSymbolFilter(query));
    }
    setChangeLogActionFilter(filter: ChangeLogActionFilter): void {
        this.#dispatch(setChangeLogActionFilter(filter));
    }
    setChangeLogTimeRange(range: ChangeLogTimeRange): void {
        this.#dispatch(setChangeLogTimeRange(range));
    }

    /**
     * Server-reported total number of change-log entries for the open pool
     * after the backend `$filter`, used for the "showing X-Y of N" label and
     * the page stepper. Returns `0` while the query is loading or disabled.
     */
    readonly changeLogTotalCount: ReadonlySignal<number> = computed(() => {
        return this.#changeLogQuery.signal.value.data?.count ?? 0;
    });
    readonly changeLogRowsPerPage: ReadonlySignal<InventoryRowsPerPage> = computed(
        () => this.#state.value.changeLogRowsPerPage,
    );
    /** Clamped to 1+ even when empty so the page UI is always navigable. */
    readonly changeLogPageCount: ReadonlySignal<number> = computed(() => {
        const perPage = this.#state.value.changeLogRowsPerPage;
        if (perPage === 'all') return 1;
        return Math.max(1, Math.ceil(this.changeLogTotalCount.value / perPage));
    });
    /**
     * Clamped current page. The stored `changeLogPage` is user-driven and
     * may fall out of range when the count shrinks; this view snaps it
     * back into `[1, pageCount]` so consumers don't have to.
     */
    readonly changeLogPage: ReadonlySignal<number> = computed(() => {
        const requested = this.#state.value.changeLogPage;
        const max = this.changeLogPageCount.value;
        if (!Number.isFinite(requested) || requested < 1) return 1;
        if (requested > max) return max;
        return Math.floor(requested);
    });
    setChangeLogPage(page: number): void {
        this.#dispatch(setChangeLogPage(page));
    }
    setChangeLogRowsPerPage(rowsPerPage: InventoryRowsPerPage): void {
        this.#dispatch(setChangeLogRowsPerPage(rowsPerPage));
    }

    // --- Pool status (Pause / Resume) ---
    // --- Create pool ---
    readonly createPoolOpen: ReadonlySignal<boolean> = computed(() => this.#state.value.createPoolOpen);
    readonly createPoolDraft: ReadonlySignal<CreatePoolDraft> = computed(() => this.#state.value.createPoolDraft);
    readonly isCreatingPool: ReadonlySignal<boolean> = computed(() => this.#state.value.isCreatingPool);
    readonly createPoolError: ReadonlySignal<string | null> = computed(() => this.#state.value.createPoolError);
    /**
     * The firm selector only exists in the superuser "all firms" scope;
     * the firm-scoped page already fixes the target firm.
     */
    readonly createPoolNeedsFirm: ReadonlySignal<boolean> = computed(() => this.#firmId.value === null);
    readonly canSubmitCreatePool: ReadonlySignal<boolean> = computed(() => {
        const s = this.#state.value;
        const draft = s.createPoolDraft;
        return (
            !s.isCreatingPool &&
            draft.name.trim() !== '' &&
            EVENT_EMAIL_PATTERN.test(draft.eventEmail.trim()) &&
            (!this.createPoolNeedsFirm.value || draft.firmId !== '')
        );
    });

    openCreatePool(): void {
        this.#dispatch(openCreatePool());
    }

    closeCreatePool(): void {
        // Ignore close attempts while the POST is settling: the dialog
        // disables its close affordances, but a stray Escape or overlay
        // click shouldn't silently drop the in-flight lifecycle.
        if (this.#state.value.isCreatingPool) return;
        this.#dispatch(closeCreatePool());
    }

    updateCreatePoolDraft(patch: Partial<CreatePoolDraft>): void {
        this.#dispatch(updateCreatePoolDraft(patch));
    }

    /**
     * POST the current draft against `POST /v1/inventory/pools`. The
     * target firm is the page's firm scope, or the draft's `firmId` in
     * the superuser "all firms" view. On success: invalidate the pool
     * list (so the detail view can resolve the new pool), close the
     * dialog, toast, pre-open the upload wizard when the draft asked
     * for it (it only renders once the detail view mounts), and return
     * the new pool's id so the view can push the detail URL — the URL,
     * not the model, owns which pool is open. On failure the error
     * stays in the dialog for a correct-and-retry loop rather than a
     * transient toast.
     */
    async submitCreatePool(): Promise<string | null> {
        if (!this.canSubmitCreatePool.value) return null;
        const draft = this.#state.value.createPoolDraft;
        const scope = this.#firmId.value;
        const firmId = scope ?? draft.firmId;
        const name = draft.name.trim();
        // The submit gate already vetted the draft; this only guards the
        // uninitialized (`scope === undefined`) window before the page
        // sets a firm scope.
        if (!firmId) return null;
        this.#dispatch(startCreatePool());
        try {
            const response = await createPool({
                name,
                description: draft.description.trim(),
                firmId,
                eventEmail: draft.eventEmail.trim(),
            });
            await invalidatePoolsQuery(this.#queryClient, scope ?? undefined);
            this.#markFresh();
            this.#dispatch(succeedCreatePool());
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: t('admin::pool-management::toast-pool-created', { name }),
                    group: 'default',
                    variant: 'success',
                },
            });
            // Don't open the wizard here: the view still has to push the
            // detail URL, and OPEN_POOL_DETAIL scrubs upload state to kill
            // stale wizards from abandoned sessions. Park the intent
            // instead; `openPoolDetail` consumes it after its own
            // transition, so the new pool's wizard always starts from a
            // clean 'drop' step.
            if (draft.initMode === 'upload') this.#pendingUploadWizardPoolId = response.pool.id;
            return response.pool.id;
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            this.#dispatch(failCreatePool(message));
            return null;
        }
    }

    readonly pendingPauseId: ReadonlySignal<string | null> = computed(() => this.#pendingPauseId.value);

    /**
     * Open the pause-confirmation modal for a pool. No-ops if the pool
     * is already paused or the caller can't write.
     */
    requestPausePool(poolId: string): void {
        const pool = this.pools.value.find((p) => p.id === poolId);
        if (!pool || pool.status !== 'active') return;
        if (pool.permission === 'reader') return;
        this.#pendingPauseId.value = poolId;
    }
    cancelPausePool(): void {
        this.#pendingPauseId.value = null;
    }
    /**
     * Commit the pending pause (called from the confirm modal). Drops
     * the pending id whether or not the toggle actually fires; the toast
     * surfaces the resulting state for visibility.
     */
    confirmPausePool(): void {
        const poolId = this.#pendingPauseId.value;
        this.#pendingPauseId.value = null;
        if (!poolId) return;
        this.#setPoolStatus(poolId, 'paused');
    }
    /**
     * Flip a paused pool back to active. No confirmation prompt — the
     * action is recoverable by pausing again.
     */
    resumePool(poolId: string): void {
        const pool = this.pools.value.find((p) => p.id === poolId);
        if (!pool || pool.status !== 'paused') return;
        if (pool.permission === 'reader') return;
        this.#setPoolStatus(poolId, 'active');
    }

    // --- Delete pool ---
    readonly pendingDeletePool: ReadonlySignal<Pool | null> = computed(() => {
        const id = this.#pendingDeleteId.value;
        if (!id) return null;
        return this.pools.value.find((p) => p.id === id) ?? null;
    });
    readonly deletePoolReason: ReadonlySignal<string> = computed(() => this.#deletePoolReason.value);
    readonly isDeletingPool: ReadonlySignal<boolean> = computed(() => this.#isDeletingPool.value);
    readonly deletePoolError: ReadonlySignal<string | null> = computed(() => this.#deletePoolError.value);
    readonly canConfirmDeletePool: ReadonlySignal<boolean> = computed(
        () =>
            this.pendingDeletePool.value !== null &&
            !this.#isDeletingPool.value &&
            this.#deletePoolReason.value.trim().length >= MIN_DELETE_POOL_REASON_LENGTH,
    );

    /**
     * Open the delete-confirm dialog for `poolId` with a blank reason.
     * Gated on `owner` pool access — the closest client-side proxy for
     * the backend's firm-owner requirement (firm-level grants surface
     * in the pool's applied permissions); a holder of a direct
     * pool-only owner grant gets the backend's 403 in the dialog.
     */
    requestDeletePool(poolId: string): void {
        const pool = this.pools.value.find((p) => p.id === poolId);
        if (!pool || pool.permission !== 'owner') return;
        // Only modern pools can be deleted — the backend 400s for legacy
        // pools (PLAT-12869). The card hides its Delete action; this guard
        // backstops any other caller.
        if (this.legacyPoolIds.value.has(poolId)) return;
        // Mirror cancelDeletePool's guard: a new request must not hijack
        // the dialog state while a DELETE is still settling.
        if (this.#isDeletingPool.value) return;
        this.#pendingDeleteId.value = poolId;
        this.#deletePoolReason.value = '';
        this.#deletePoolError.value = null;
    }

    cancelDeletePool(): void {
        // Ignore close attempts while the DELETE is settling — a stray
        // Escape or overlay click shouldn't silently drop the in-flight
        // lifecycle.
        if (this.#isDeletingPool.value) return;
        this.#pendingDeleteId.value = null;
        this.#deletePoolReason.value = '';
        this.#deletePoolError.value = null;
    }

    setDeletePoolReason(reason: string): void {
        this.#deletePoolReason.value = reason;
    }

    /**
     * Issue `DELETE /v1/inventory/pools/{poolId}` with the trimmed
     * reason. On success: invalidate the pool list (dropping the card
     * from the list view), close the dialog, and toast. On failure the
     * error stays in the dialog for a correct-and-retry loop.
     */
    async confirmDeletePool(): Promise<boolean> {
        if (!this.canConfirmDeletePool.value) return false;
        const pool = this.pendingDeletePool.value;
        if (!pool) return false;
        const reason = this.#deletePoolReason.value.trim();
        this.#isDeletingPool.value = true;
        this.#deletePoolError.value = null;
        try {
            await deletePool({ poolId: pool.id, request: { reason } });
            await invalidatePoolsQuery(this.#queryClient, this.#firmId.value ?? undefined);
            this.#markFresh();
            this.#pendingDeleteId.value = null;
            this.#deletePoolReason.value = '';
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: t('admin::pool-management::toast-pool-deleted', { name: pool.name }),
                    group: 'default',
                    variant: 'success',
                },
            });
            return true;
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            this.#deletePoolError.value = message;
            return false;
        } finally {
            this.#isDeletingPool.value = false;
        }
    }

    // --- Item availability (suspend / publish) ---
    readonly pendingAvailabilityRowIds: ReadonlySignal<ReadonlySet<string>> = computed(
        () => new Set(this.#itemAvailabilityOverrides.value.keys()),
    );

    setItemAvailability(rowId: string, available: boolean): void {
        const poolId = this.#state.value.selectedPoolId;
        if (!poolId) return;
        // Draft rows don't exist on the backend yet; readers can't write;
        // the legacy backend silently ignores availability, so fail closed
        // (the view also hides the column for legacy pools).
        if (isDraftRowId(rowId)) return;
        if (!this.canWrite.value || this.selectedPoolIsLegacy.value) return;
        const row = this.serverInventoryRows.value.find((r) => r.id === rowId);
        if (!row || row.available === available) return;
        // One in-flight PATCH per row: the toggle is disabled while its id
        // is in `pendingAvailabilityRowIds`, this backstops other callers.
        if (this.#itemAvailabilityOverrides.value.has(rowId)) return;

        const optimistic = new Map(this.#itemAvailabilityOverrides.value);
        optimistic.set(rowId, available);
        this.#itemAvailabilityOverrides.value = optimistic;
        const version = (this.#itemAvailabilityMutationVersion.get(rowId) ?? 0) + 1;
        this.#itemAvailabilityMutationVersion.set(rowId, version);
        const clearOverride = () => {
            const next = new Map(this.#itemAvailabilityOverrides.value);
            next.delete(rowId);
            this.#itemAvailabilityOverrides.value = next;
        };

        void updatePoolItem({ poolId, itemId: rowId, request: { available } }).then(
            async () => {
                if (this.#itemAvailabilityMutationVersion.get(rowId) !== version) return;
                // Reseat `available` from the backend before dropping the
                // override so the toggle can't flash back to the stale
                // value; availability changes also land on the change log.
                // The PATCH itself succeeded, so a failed invalidation must
                // not leave the row stuck in the pending set — clear the
                // override either way and let the next refetch reconcile.
                try {
                    await Promise.all([
                        invalidatePoolItemsQuery(this.#queryClient, poolId),
                        invalidatePoolChangeLogQuery(this.#queryClient, poolId),
                    ]);
                } catch {
                    // Fall through to the cleanup below.
                }
                if (this.#itemAvailabilityMutationVersion.get(rowId) !== version) return;
                clearOverride();
                this.#markFresh();
                this.#store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: t(
                            available
                                ? 'admin::pool-management::toast-item-published'
                                : 'admin::pool-management::toast-item-suspended',
                            { symbol: row.symbol },
                        ),
                        group: 'default',
                        variant: 'success',
                    },
                });
            },
            async (error: unknown) => {
                if (this.#itemAvailabilityMutationVersion.get(rowId) !== version) return;
                clearOverride();
                // Toast before the invalidation await: clearing the override
                // above releases the row for a new toggle, and a stale
                // failure toast must not land after that newer operation
                // already settled.
                const message = error instanceof Error ? error.message : String(error);
                this.#store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: t('admin::pool-management::toast-item-availability-failed', {
                            symbol: row.symbol,
                            message,
                        }),
                        group: 'default',
                        variant: 'error',
                    },
                });
                try {
                    await invalidatePoolItemsQuery(this.#queryClient, poolId);
                } catch {
                    // Best-effort cache refresh on rollback — a failed
                    // invalidation just leaves the stale entry until the
                    // next fetch, and must not surface as an unhandled
                    // rejection.
                }
            },
        );
    }

    // --- Pool permissions ---
    /**
     * `userId -> { firstName, lastName, email }` derived from the
     * firm-members query. Used to resolve the holder list and to
     * power the grant-access selector.
     */
    readonly #memberLookup: ReadonlySignal<
        ReadonlyMap<string, { firstName: string; lastName: string; email: string }>
    > = computed(() => {
        const members = this.#firmMembersQuery.signal.value.data ?? [];
        const map = new Map<string, { firstName: string; lastName: string; email: string }>();
        for (const m of members) {
            const profile = m as unknown as {
                userId?: string;
                profile?: { firstName: string; lastName: string; email: string };
            };
            const userId = profile.userId;
            if (!userId || !profile.profile) continue;
            map.set(userId, profile.profile);
        }
        return map;
    });

    readonly poolPermissionHolders: ReadonlySignal<ReadonlyArray<PoolPermissionHolder>> = computed(() => {
        const id = this.#state.value.selectedPoolId;
        if (!id) return [];
        const raw = this.#poolsQuery.signal.value.data?.pools.find((p) => p.id === id);
        return collectPoolPermissionHolders(raw, this.#memberLookup.value);
    });

    readonly poolPermissionCandidates: ReadonlySignal<ReadonlyArray<PoolPermissionCandidate>> = computed(() => {
        const holders = this.poolPermissionHolders.value;
        const taken = new Set(holders.map((h) => h.userId));
        const out: PoolPermissionCandidate[] = [];
        for (const [userId, profile] of this.#memberLookup.value) {
            if (taken.has(userId)) continue;
            const userName = `${profile.firstName} ${profile.lastName}`.trim() || profile.email;
            out.push({ userId, userName, email: profile.email });
        }
        // Stable alphabetical sort so the dropdown order doesn't churn
        // when the underlying map iterates differently after a refetch.
        out.sort((a, b) => a.userName.localeCompare(b.userName));
        return out;
    });

    readonly pendingPermissionChange: ReadonlySignal<{
        userId: string;
        userName: string;
        role: PermissionRole;
    } | null> = computed(() => {
        const change = this.#pendingPermissionChange.value;
        if (!change) return null;
        const profile = this.#memberLookup.value.get(change.userId);
        const userName = profile ? `${profile.firstName} ${profile.lastName}`.trim() || profile.email : change.userId;
        return { userId: change.userId, userName, role: change.role };
    });

    readonly permissionsMutating: ReadonlySignal<boolean> = computed(
        () => this.#permissionsMutationsInFlight.value > 0,
    );

    requestSetPoolPermissionRole(userId: string, role: PermissionRole): void {
        if (!this.canManagePermissions.value || !this.canOwn.value) return;
        const pool = this.selectedPool.value;
        if (!pool) return;
        this.#pendingPermissionChange.value = { poolId: pool.id, userId, role };
    }

    cancelSetPoolPermissionRole(): void {
        this.#pendingPermissionChange.value = null;
    }

    async confirmSetPoolPermissionRole(): Promise<void> {
        const change = this.#pendingPermissionChange.value;
        if (!change) return;
        // Fail closed if either gate has been revoked since the dialog
        // opened: drop the pending change instead of firing a PATCH the
        // caller is no longer allowed to make. The owner check targets
        // the *captured* pool (in case the user has switched away)
        // rather than `selectedPool`.
        const pool = this.pools.value.find((p) => p.id === change.poolId);
        if (!this.canManagePermissions.value || pool?.permission !== 'owner') {
            this.#pendingPermissionChange.value = null;
            return;
        }
        this.#pendingPermissionChange.value = null;
        await this.#putUserPermission(change.poolId, change.userId, [{ access: change.role, kind: 'allow' }]);
    }

    async addPoolPermission(userId: string, role: PermissionRole): Promise<void> {
        if (!this.canManagePermissions.value || !this.canOwn.value) return;
        const pool = this.selectedPool.value;
        if (!pool) return;
        await this.#putUserPermission(pool.id, userId, [{ access: role, kind: 'allow' }]);
    }

    async removePoolPermission(userId: string): Promise<void> {
        if (!this.canManagePermissions.value || !this.canOwn.value) return;
        const pool = this.selectedPool.value;
        if (!pool) return;
        // Empty entries array clears every rung the user holds.
        await this.#putUserPermission(pool.id, userId, []);
    }

    /**
     * Issue the PUT against the `user` entity-type endpoint, increment /
     * decrement the in-flight counter so the view can disable controls,
     * dispatch success / error toasts, and invalidate the pool list so
     * the table refreshes with the server's new `permissions` payload.
     * `poolId` is captured by the caller, not read at fire time, so a
     * pool-switch between request and confirm doesn't redirect the PUT.
     */
    async #putUserPermission(poolId: string, userId: string, entries: PermissionUpdateEntry[]): Promise<void> {
        const profile = this.#memberLookup.value.get(userId);
        const displayName = profile ? `${profile.firstName} ${profile.lastName}`.trim() || profile.email : userId;
        this.#permissionsMutationsInFlight.value = this.#permissionsMutationsInFlight.value + 1;
        try {
            await updatePoolPermissionsForEntity({
                poolId,
                entityType: 'user',
                entityId: userId,
                entries,
            });
            await invalidatePoolsQuery(this.#queryClient, this.#firmId.value ?? undefined);
            this.#markFresh();
            const messageKey =
                entries.length === 0
                    ? 'admin::pool-management::toast-permissions-removed'
                    : 'admin::pool-management::toast-permissions-updated';
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: t(messageKey, { name: displayName }),
                    group: 'default',
                    variant: 'success',
                },
            });
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: t('admin::pool-management::toast-permissions-error', {
                        name: displayName,
                        message,
                    }),
                    group: 'default',
                    variant: 'error',
                },
            });
        } finally {
            this.#permissionsMutationsInFlight.value = this.#permissionsMutationsInFlight.value - 1;
        }
    }

    /**
     * Optimistically flip a pool's status, then call the backend. The
     * UI updates immediately so the pill / toast feel snappy; on
     * rejection we roll back the override and surface an error toast.
     *
     * Hits `PATCH /v1/inventory/pools/{poolId}` with `{ active }`. On
     * success we patch the pool-list cache with the response, then
     * clear the override entry entirely so the cache becomes the sole
     * source of truth — keeping a stale override would mask a later
     * server-side change (e.g. another admin pausing the pool). On
     * failure the override snaps back to whatever was there before.
     */
    #setPoolStatus(poolId: string, status: PoolStatus): void {
        const pool = this.pools.value.find((p) => p.id === poolId);
        if (!pool) return;
        // Centralized write-permission gate: callers can reach this path
        // after the pool's permission has dropped (the pause-confirm
        // modal can stay open while the user's access is revoked); fail
        // closed locally rather than relying on the server PATCH to
        // bounce.
        if (pool.permission === 'reader') return;
        if (pool.status === status) return;
        const previousOverride = this.#poolStatusOverrides.value.get(poolId);
        const optimistic = new Map<string, PoolStatus>(this.#poolStatusOverrides.value);
        optimistic.set(poolId, status);
        this.#poolStatusOverrides.value = optimistic;
        const version = (this.#poolStatusMutationVersion.get(poolId) ?? 0) + 1;
        this.#poolStatusMutationVersion.set(poolId, version);

        void updatePool({ poolId, request: { active: status === 'active' } }).then(
            (response) => {
                if (this.#poolStatusMutationVersion.get(poolId) !== version) return;
                // Patch the cached pool entries with the server's confirmed
                // `active` value, then drop the override so subsequent
                // pool-list refetches drive the pill directly. Touch both
                // the unscoped (`['lab-pools']`) and the firm-scoped
                // (`['lab-pools', firmId]`) caches so superuser and firm
                // views stay in sync.
                this.#patchPoolInCache(response.pool);
                const cleared = new Map<string, PoolStatus>(this.#poolStatusOverrides.value);
                cleared.delete(poolId);
                this.#poolStatusOverrides.value = cleared;
                this.#markFresh();
                const messageKey =
                    status === 'paused'
                        ? 'admin::pool-management::toast-pool-paused'
                        : 'admin::pool-management::toast-pool-resumed';
                this.#store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: t(messageKey, { name: pool.name }),
                        group: 'default',
                        variant: 'success',
                    },
                });
            },
            (error: unknown) => {
                if (this.#poolStatusMutationVersion.get(poolId) !== version) return;
                // The captured `previousOverride` may not match the
                // server truth if earlier in-flight PATCHes for this
                // pool were version-gated out and never wrote it back.
                // Roll the optimistic entry back as a best-effort
                // visual restore, then invalidate the pool-list query
                // so the next refetch reseats `active` from the
                // backend and clears the lingering override.
                const reverted = new Map<string, PoolStatus>(this.#poolStatusOverrides.value);
                if (previousOverride === undefined) reverted.delete(poolId);
                else reverted.set(poolId, previousOverride);
                this.#poolStatusOverrides.value = reverted;
                void invalidatePoolsQuery(this.#queryClient, this.#firmId.value ?? undefined);
                const message = error instanceof Error ? error.message : String(error);
                const errorKey =
                    status === 'paused'
                        ? 'admin::pool-management::toast-pool-pause-error'
                        : 'admin::pool-management::toast-pool-resume-error';
                this.#store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: t(errorKey, { name: pool.name, message }),
                        group: 'default',
                        variant: 'error',
                    },
                });
            },
        );
    }

    /**
     * Patch a single pool's entry inside every cached pool-list query
     * (both the unscoped `[LAB_POOLS]` superuser view and any
     * firm-scoped `[LAB_POOLS, firmId]` views). Used by `#setPoolStatus`
     * to fold the server's confirmed `active` value into the cache
     * without triggering a refetch round-trip.
     */
    #patchPoolInCache(updated: InventoryPool): void {
        const matches = this.#queryClient.getQueriesData<ListPoolsResponse>({ queryKey: [LAB_POOLS] });
        for (const [key, data] of matches) {
            if (!data) continue;
            if (!data.pools.some((p) => p.id === updated.id)) continue;
            this.#queryClient.setQueryData<ListPoolsResponse>(key, {
                ...data,
                pools: data.pools.map((p) => (p.id === updated.id ? updated : p)),
            });
        }
    }

    // --- Search commands ---
    setPoolSearchQuery(query: string): void {
        this.#dispatch(setPoolSearchQuery(query));
    }
    setPoolStatusFilter(filter: PoolStatusFilter): void {
        this.#dispatch(setPoolStatusFilter(filter));
    }
    setSymbolSearchQuery(query: string): void {
        // Update the input text immediately so typing stays responsive,
        // then commit the backend `symbol` filter on a debounce.
        this.#dispatch(setSymbolSearchQuery(query));
        if (query.trim() === '') {
            // Clearing the box should restore the full (paged) pool right
            // away — there's nothing to debounce.
            this.#applySymbolSearch.cancel();
            this.#dispatch(setAppliedSymbolSearch(''));
            return;
        }
        this.#applySymbolSearch();
    }

    /**
     * Commits the current search box text to the backend `symbol`
     * filter. Debounced so live typing fires a single exact-match
     * request once the user pauses rather than one per keystroke.
     */
    readonly #applySymbolSearch = debounce(() => {
        // Symbols are stored upper-case and the backend filter is a
        // case-sensitive `sym = @sym` match, so normalize the typed term
        // the same way the add-symbol path does before sending it.
        this.#dispatch(setAppliedSymbolSearch(this.#state.value.symbolSearchQuery.trim().toUpperCase()));
    }, SYMBOL_SEARCH_DEBOUNCE_MS);

    // --- Write commands (no-op when read-only) ---
    setAddSymbolDraft(value: string): void {
        if (!this.canWrite.value) return;
        this.#dispatch(setAddSymbolDraft(value));
    }
    async addSymbolFromDraft(): Promise<void> {
        if (!this.canWrite.value) return;
        // Block while a submit is settling: a draft staged mid-flight would
        // be wiped when the submit's success path clears the pool's
        // pending-adds map, silently discarding it without a POST.
        if (this.#state.value.isSubmitting) return;
        const draft = this.#state.value.addSymbolDraft.trim();
        if (!draft) return;
        // Only clear the draft when the symbol actually lands. A rejected
        // add (duplicate, no selected pool, …) preserves the user's input
        // so they can correct it instead of silently losing what they typed.
        if (await this.#addSymbolInternal(draft)) {
            this.#dispatch(setAddSymbolDraft(''));
        }
    }
    async addSymbol(symbol: string): Promise<void> {
        if (!this.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        await this.#addSymbolInternal(symbol);
    }

    /** `true` when `symbol` is on the loaded items page or staged as a pending add. */
    #hasSymbolLocally(poolId: string, symbol: string): boolean {
        const serverItems = this.#itemsQuery.signal.value.data?.items ?? [];
        if (serverItems.some((r) => r.symbol === symbol)) return true;
        const existingPending = this.#pendingAddsByPool.value.get(poolId) ?? [];
        return existingPending.some((r) => r.symbol === symbol);
    }

    #toastAddSymbolDuplicate(symbol: string): void {
        this.#store.dispatch({
            type: 'toast::enqueue',
            toast: {
                message: t('admin::pool-management::toast-add-symbol-duplicate', { symbol }),
                group: 'default',
                variant: 'error',
            },
        });
    }

    async #addSymbolInternal(rawSymbol: string): Promise<boolean> {
        const symbol = rawSymbol.trim().toUpperCase();
        if (!symbol) return false;
        const poolId = this.#state.value.selectedPoolId;
        if (!poolId) return false;
        if (this.#hasSymbolLocally(poolId, symbol)) {
            this.#toastAddSymbolDuplicate(symbol);
            return false;
        }
        // The items page only holds the current table page, so a page-local
        // check misses symbols elsewhere in the pool and the add would
        // duplicate them (PLAT-12841). Ask the backend's exact-match filter,
        // which spans the whole pool regardless of pagination.
        try {
            const options = getPoolItemsQuery(poolId, { symbol });
            // `staleTime: 0` forces a network refetch: a correctness probe
            // must not be satisfied by the query's 30s-fresh cache entry,
            // which could miss a concurrent insert by another client.
            const response = await this.#queryClient.fetchQuery({
                ...options,
                queryKey: options.queryKey ?? [],
                staleTime: 0,
            });
            if (response.items.length > 0) {
                this.#toastAddSymbolDuplicate(symbol);
                return false;
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: t('admin::pool-management::toast-add-symbol-error', { symbol, message }),
                    group: 'default',
                    variant: 'error',
                },
            });
            return false;
        }
        // Re-validate after the await: the user may have switched pools, a
        // concurrent add may have staged the same symbol, or a submit may
        // have started meanwhile. Staging into an in-flight submit would
        // let its success path wipe the row via the blanket per-pool
        // pending-adds delete without the row ever being POSTed.
        if (this.#state.value.isSubmitting) return false;
        if (this.#state.value.selectedPoolId !== poolId) return false;
        if (this.#hasSymbolLocally(poolId, symbol)) return false;
        const existingPending = this.#pendingAddsByPool.value.get(poolId) ?? [];
        const newRow: InventoryRow = {
            id: `${PENDING_ROW_PREFIX}${poolId}_${symbol.toLowerCase()}_${Date.now()}`,
            symbol,
            currentQuantity: 0,
            currentLabPrice: 0,
            borrowStatus: null,
            quantityUsed: null,
            dailyRevenue: null,
            available: true,
            currentPrice: null,
            fairValuePrice: null,
        };
        const next = new Map(this.#pendingAddsByPool.value);
        // Prepend so the newest add is at the top of the pending block,
        // which (combined with the `[...pending, ...remote]` merge) puts
        // it at row 0 of the table.
        next.set(poolId, [newRow, ...existingPending]);
        this.#pendingAddsByPool.value = next;
        return true;
    }
    editRowQuantity(rowId: string, value: number): void {
        if (!this.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        // Quantities must be non-negative integers: the inventory CSV
        // round-trip only parses integer share counts (see
        // `parseInventoryCsv`), and the table renders them with
        // thousands separators that imply whole shares. Reject
        // fractional / Infinity / NaN inputs so a stray decimal isn't
        // silently truncated on export.
        if (!Number.isInteger(value) || value < 0) return;
        // A value matching the underlying row is a no-op (e.g. focusing
        // then blurring the field without typing). Clear any staged edit
        // instead of staging it, so the row doesn't show as dirty.
        const original = this.serverInventoryRows.value.find((r) => r.id === rowId);
        if (original && original.currentQuantity === value) {
            this.#dispatch(clearRowFieldEdit(rowId, 'currentQuantity'));
            return;
        }
        this.#dispatch(stageRowEdit({ rowId, currentQuantity: value }, toBaseline(original)));
    }
    editRowLabPrice(rowId: string, value: number): void {
        if (!this.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        if (!Number.isFinite(value) || value < 0) return;
        // See `editRowQuantity`: re-applying the row's existing price (the
        // `0`-price focus/blur case) must not dirty the row.
        const original = this.serverInventoryRows.value.find((r) => r.id === rowId);
        if (original && original.currentLabPrice === value) {
            this.#dispatch(clearRowFieldEdit(rowId, 'currentLabPrice'));
            return;
        }
        this.#dispatch(stageRowEdit({ rowId, currentLabPrice: value }, toBaseline(original)));
    }
    clearRowQuantityEdit(rowId: string): void {
        if (!this.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        this.#dispatch(clearRowFieldEdit(rowId, 'currentQuantity'));
    }
    clearRowLabPriceEdit(rowId: string): void {
        if (!this.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        this.#dispatch(clearRowFieldEdit(rowId, 'currentLabPrice'));
    }
    removeRow(rowId: string): void {
        if (!this.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        if (rowId.startsWith(PENDING_ROW_PREFIX)) {
            // It's a locally-added row that never went to the backend; just drop it.
            const poolId = this.#state.value.selectedPoolId;
            if (!poolId) return;
            const pending = this.#pendingAddsByPool.value.get(poolId) ?? [];
            const next = pending.filter((r) => r.id !== rowId);
            const map = new Map(this.#pendingAddsByPool.value);
            if (next.length === 0) map.delete(poolId);
            else map.set(poolId, next);
            this.#pendingAddsByPool.value = map;
            // Drop any staged field edits keyed on this draft id so they
            // don't orphan in `rowEdits` and leave `isDirty` diverging
            // from `unsavedChangeCount`.
            this.#dispatch(clearRowFieldEdit(rowId, 'currentQuantity'));
            this.#dispatch(clearRowFieldEdit(rowId, 'currentLabPrice'));
            return;
        }
        // Capture the row's server values so the submit-review preview can
        // list the removal without a full-pool fetch (PLAT-12889). The row
        // is on the visible page (the action came from its row), so the
        // lookup only misses if the id went stale — the preview then skips
        // it, matching the old snapshot behavior for stale ids.
        const original = this.serverInventoryRows.value.find((r) => r.id === rowId);
        this.#dispatch(stageRowRemoval(rowId, toBaseline(original)));
    }
    restoreRow(rowId: string): void {
        if (!this.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        this.#dispatch(unstageRowRemoval(rowId));
    }
    resetEdits(): void {
        if (!this.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        // The RESET_EDITS reducer case also clears `submitError` so the
        // user isn't stuck on a dead-end error after resetting.
        this.#dispatch(resetEdits());
        const poolId = this.#state.value.selectedPoolId;
        if (poolId && this.#pendingAddsByPool.value.has(poolId)) {
            const map = new Map(this.#pendingAddsByPool.value);
            map.delete(poolId);
            this.#pendingAddsByPool.value = map;
        }
    }
    openSubmitReview(): void {
        if (!this.canWrite.value) return;
        this.#dispatch(openSubmitReview());
    }
    closeSubmitReview(): void {
        this.#dispatch(closeSubmitReview());
    }

    /**
     * Translate staged edits/removals/pending-adds into the per-item
     * backend operations. The locates-ws API doesn't expose a bulk
     * preview/apply, so the model fans out the change set into one
     * `POST .../items` (batched), N `PATCH .../items/{id}`, and N
     * `DELETE .../items/{id}` calls and waits for all to settle.
     *
     * Each op is tagged so a partial failure can leave only the
     * failed pieces staged for retry — succeeded edits/removals are
     * cleared, and pending-adds are dropped only when their batch POST
     * actually committed.
     */
    async submitChanges(): Promise<boolean> {
        if (!this.canWrite.value) return false;
        if (this.#state.value.isSubmitting) return false;
        const poolId = this.#state.value.selectedPoolId;
        if (!poolId) return false;

        const operations = this.#buildSubmitOperations(poolId);
        if (operations.length === 0) {
            this.#dispatch(resetEdits());
            this.#dispatch(closeSubmitReview());
            return true;
        }
        // Only enter the submitting lifecycle once we know there's real
        // work — otherwise the no-op path leaves `isSubmitting` stuck
        // true because nothing dispatches `succeedSubmit`.
        this.#dispatch(startSubmit());
        const totalOps = operations.length;
        const toastId = this.#startActionToast(
            t('admin::pool-management::toast-submit-in-progress', { count: totalOps }),
        );

        try {
            const results = await Promise.allSettled(operations.map((o) => o.promise));
            await Promise.all([
                invalidatePoolItemsQuery(this.#queryClient, poolId),
                invalidatePoolChangeLogQuery(this.#queryClient, poolId),
            ]);
            this.#markFresh();

            // Walk results once, partitioning ops by outcome so partial
            // failures keep only the failed staged changes around.
            const successfulEditIds: string[] = [];
            const successfulRemovalIds: string[] = [];
            let pendingAddsSucceeded = false;
            let firstFailure: unknown = null;
            for (let i = 0; i < operations.length; i++) {
                const op = operations[i]!;
                const res = results[i]!;
                if (res.status === 'fulfilled') {
                    if (op.kind === 'update') successfulEditIds.push(op.rowId);
                    else if (op.kind === 'remove') successfulRemovalIds.push(op.rowId);
                    else {
                        pendingAddsSucceeded = true;
                        // Clear any staged edits keyed by the pending-row ids
                        // so they don't orphan in `rowEdits` once the rows
                        // themselves are gone from `#pendingAddsByPool`.
                        successfulEditIds.push(...op.rowIds);
                    }
                } else if (firstFailure === null) {
                    firstFailure = res.reason;
                }
            }

            if (successfulEditIds.length > 0 || successfulRemovalIds.length > 0) {
                // A successful DELETE also invalidates any staged edit on
                // that row (edits are skipped for removed rows at op-build
                // time, so they never got their own op). Clear both, or a
                // partial failure would resurrect the edit as a phantom
                // "updated" entry in the review dialog and re-PATCH a row
                // the backend already deleted (404) on retry.
                this.#dispatch(
                    clearStagedChanges([...successfulEditIds, ...successfulRemovalIds], successfulRemovalIds),
                );
            }
            if (pendingAddsSucceeded) {
                const map = new Map(this.#pendingAddsByPool.value);
                map.delete(poolId);
                this.#pendingAddsByPool.value = map;
            }

            if (firstFailure !== null) {
                const message = firstFailure instanceof Error ? firstFailure.message : String(firstFailure);
                this.#dispatch(failSubmit(message));
                this.#replaceToast(toastId, {
                    variant: 'error',
                    message: t('admin::pool-management::toast-submit-error', { message }),
                });
                return false;
            }

            this.#dispatch(succeedSubmit());
            this.#dispatch(resetEdits());
            this.#dispatch(closeSubmitReview());
            this.#replaceToast(toastId, {
                variant: 'success',
                message: t('admin::pool-management::toast-submit-success', { count: totalOps }),
            });
            return true;
        } catch (e) {
            const message = e instanceof Error ? e.message : t('admin::pool-management::submit-error-generic');
            this.#dispatch(failSubmit(message));
            this.#replaceToast(toastId, {
                variant: 'error',
                message: t('admin::pool-management::toast-submit-error', { message }),
            });
            return false;
        }
    }

    #buildSubmitOperations(poolId: string): SubmitOperation[] {
        const edits = this.#state.value.rowEdits;
        const removals = new Set(this.#state.value.pendingRowRemovals);
        const pendingAdds = this.#pendingAddsByPool.value.get(poolId) ?? [];

        const ops: SubmitOperation[] = [];

        // Staged ids are trusted as-is: they were captured from real server
        // rows when staged. Requiring them to still be on the CURRENT items
        // page would silently drop changes staged from another page or from
        // a since-cleared symbol search (PLAT-12842) — the items query only
        // holds one page since server-side pagination landed. If an id has
        // gone stale (row deleted or replaced elsewhere), the call 404s and
        // the partial-failure path keeps that change staged with an error.

        // PATCH existing items that have staged edits and aren't being removed.
        for (const [rowId, edit] of Object.entries(edits)) {
            if (removals.has(rowId)) continue;
            if (rowId.startsWith(PENDING_ROW_PREFIX)) continue;
            const patch: { quantity?: number; price?: number } = {};
            if (edit.currentQuantity !== undefined) patch.quantity = edit.currentQuantity;
            if (edit.currentLabPrice !== undefined) patch.price = edit.currentLabPrice;
            ops.push({ kind: 'update', rowId, promise: updatePoolItem({ poolId, itemId: rowId, request: patch }) });
        }

        // DELETE removed items (skip locally-staged pending adds — they
        // never went to the backend).
        for (const rowId of removals) {
            if (rowId.startsWith(PENDING_ROW_PREFIX)) continue;
            ops.push({ kind: 'remove', rowId, promise: removePoolItem({ poolId, itemId: rowId }) });
        }

        // POST any locally pending adds, applying their staged edits.
        if (pendingAdds.length > 0) {
            const newItems: NewPoolItem[] = pendingAdds.map((row) => {
                const edit = edits[row.id];
                return {
                    symbol: row.symbol,
                    quantity: edit?.currentQuantity ?? row.currentQuantity,
                    price: edit?.currentLabPrice ?? row.currentLabPrice,
                };
            });
            ops.push({
                kind: 'add-batch',
                rowIds: pendingAdds.map((row) => row.id),
                promise: addPoolItems({ poolId, request: { items: newItems } }),
            });
        }

        return ops;
    }

    clearSubmitError(): void {
        this.#dispatch(clearSubmitError());
    }

    // --- Upload wizard (lifecycle driven entirely by reducer state) ---
    readonly uploadWizardOpen: ReadonlySignal<boolean> = computed(() => this.#state.value.uploadWizardOpen);
    readonly uploadStep: ReadonlySignal<UploadWizardStep> = computed(() => this.#state.value.uploadStep);
    readonly uploadFile: ReadonlySignal<UploadFileInfo | null> = computed(() => this.#state.value.uploadFile);
    readonly uploadParseResult: ReadonlySignal<InventoryParseResult | null> = computed(
        () => this.#state.value.uploadParseResult,
    );
    readonly uploadCategoryFilter: ReadonlySignal<UploadCategory> = computed(
        () => this.#state.value.uploadCategoryFilter,
    );
    readonly uploadSymbolFilter: ReadonlySignal<string> = computed(() => this.#state.value.uploadSymbolFilter);
    readonly uploadMode: ReadonlySignal<UploadMode> = computed(() => this.#state.value.uploadMode);
    readonly uploadApplying: ReadonlySignal<boolean> = computed(() => this.#state.value.uploadApplying);
    readonly uploadStreamErrors: ReadonlySignal<ReadonlyArray<UploadStreamError>> = computed(
        () => this.#state.value.uploadStreamErrors,
    );
    readonly uploadProgress: ReadonlySignal<UploadApplyProgress | null> = computed(
        () => this.#state.value.uploadProgress,
    );
    readonly uploadError: ReadonlySignal<string | null> = computed(() => {
        const stateError = this.#state.value.uploadError;
        if (stateError) return stateError;
        // Surface a failed full-pool snapshot fetch: without it the diff
        // stays null exactly like the loading state and the wizard's
        // Continue gate would stay closed with no message or way forward.
        if (this.#state.value.uploadWizardOpen) {
            return this.fullInventorySnapshotError.value;
        }
        return null;
    });
    readonly uploadAppliedSummary: ReadonlySignal<{
        added: number;
        updated: number;
        removed: number;
        rejected: number;
    } | null> = computed(() => this.#state.value.uploadAppliedSummary);
    readonly uploadDiff: ReadonlySignal<UploadDiff | null> = computed(() => {
        const parsed = this.#state.value.uploadParseResult;
        if (!parsed) return null;
        // Diff against the full-pool snapshot — never the paged table
        // query. `null` while the snapshot loads keeps the wizard's
        // Continue gate closed so an apply can't run against a partial
        // view of the pool. Pending local adds don't count as "existing"
        // inventory from the upload's perspective.
        const rows = this.fullInventoryRows.value;
        if (rows === null) return null;
        return diffInventory(parsed.rows, rows);
    });

    /**
     * Every server row of the selected pool, independent of table
     * pagination. Backed by {@link #fullPoolItemsQuery}, so it is only
     * populated while the upload wizard is open; `null` while the query
     * is disabled or still loading. The upload diff reads through this
     * so a CSV is compared against the whole pool.
     */
    readonly fullInventoryRows: ReadonlySignal<ReadonlyArray<InventoryRow> | null> = computed(() => {
        const data = this.#fullPoolItemsQuery.signal.value.data;
        if (!data) return null;
        return data.items.map(fromApiItem);
    });

    /**
     * Error message from the full-pool snapshot fetch; `null` while the
     * query is disabled, loading, or succeeded. A failed fetch leaves
     * {@link fullInventoryRows} `null` exactly like the loading state,
     * so the upload wizard reads through this to tell the two apart and
     * show the failure instead of an indefinite loading state.
     */
    readonly fullInventorySnapshotError: ReadonlySignal<string | null> = computed(() => {
        return this.#fullPoolItemsQuery.signal.value.error?.message ?? null;
    });

    openUploadWizard(): void {
        if (!this.canWrite.value) return;
        // OPEN_UPLOAD_WIZARD also clears prior error/summary in the reducer.
        this.#dispatch(openUploadWizard());
    }
    closeUploadWizard(): void {
        // Short-circuit when a real apply is in-flight so the user can't
        // accidentally abandon it; `setFirm` and other forced teardowns
        // dispatch CLOSE_UPLOAD_WIZARD directly to bypass this guard.
        if (this.#state.value.uploadApplying) return;
        this.#dispatch(closeUploadWizard());
    }
    setUploadStep(step: UploadWizardStep): void {
        this.#dispatch(setUploadStep(step));
    }
    setUploadCategoryFilter(category: UploadCategory): void {
        this.#dispatch(setUploadCategoryFilter(category));
    }
    setUploadSymbolFilter(query: string): void {
        this.#dispatch(setUploadSymbolFilter(query));
    }
    setUploadMode(mode: UploadMode): void {
        this.#dispatch(setUploadMode(mode));
    }
    async selectUploadFile(file: File): Promise<void> {
        if (!this.canWrite.value) return;
        this.#dispatch(setUploadError(null));

        const name = file.name.toLowerCase();
        // CSV-only: the parser splits exclusively on commas, so accepting
        // .tsv or other delimited formats would produce confusing "missing
        // column" errors for files that look correct to the user.
        if (!name.endsWith('.csv')) {
            this.#dispatch(setUploadFile(null, null));
            this.#dispatch(
                setUploadError(t('admin::pool-management::upload-error-unsupported-format', { fileName: file.name })),
            );
            return;
        }
        // Hard size cap so a stray multi-hundred-megabyte file can't lock
        // up the main thread inside `file.text()`. 8 MB is well above the
        // 10k-row CSV the wizard advertises (~700 KB) but small enough to
        // parse synchronously without UI jank.
        if (file.size > MAX_UPLOAD_BYTES) {
            this.#dispatch(setUploadFile(null, null));
            this.#dispatch(
                setUploadError(
                    t('admin::pool-management::upload-error-too-large', {
                        limit: `${MAX_UPLOAD_BYTES / 1024 / 1024} MB`,
                    }),
                ),
            );
            return;
        }

        try {
            const text = await file.text();
            const parsed = parseInventoryCsv(text);

            // Reject the file entirely when required columns (Symbol, Quantity)
            // are missing — no point in letting the user proceed without them.
            const headerErrors = parsed.errors.filter((e) => e.rowIndex === 0);
            if (headerErrors.length > 0) {
                this.#dispatch(setUploadFile(null, null));
                this.#dispatch(setUploadError(headerErrors.map((e) => e.message).join(' ')));
                return;
            }

            this.#dispatch(setUploadFile({ name: file.name, size: file.size, uploadedAt: Date.now() }, parsed));
            if (parsed.rows.length === 0) {
                this.#dispatch(setUploadError(t('admin::pool-management::upload-error-no-valid-rows')));
            }
        } catch (e) {
            this.#dispatch(setUploadFile(null, null));
            this.#dispatch(
                setUploadError(e instanceof Error ? e.message : t('admin::pool-management::upload-error-read-failed')),
            );
        }
    }
    clearUploadFile(): void {
        this.#dispatch(setUploadFile(null, null));
        this.#dispatch(setUploadError(null));
    }
    async applyUpload(): Promise<boolean> {
        if (!this.canWrite.value) return false;
        if (this.#state.value.uploadApplying) return false;
        const diff = this.uploadDiff.value;
        const poolId = this.#state.value.selectedPoolId;
        if (!diff || !poolId) return false;

        // Reject the apply when any added row has no LAB Price. The wizard
        // also blocks Apply in this state, but the model has to be the
        // authoritative gate since price-less inventory must never reach
        // the backend.
        if (diff.added.some((item) => item.labPrice === null)) {
            this.#dispatch(setUploadError(t('admin::pool-management::upload-error-missing-price')));
            return false;
        }

        // Only `'replace'` mode actually deletes server rows that aren't
        // in the upload; `'merge'` (the only phase-1 mode) treats those
        // rows as kept. `diff.removed` is still computed so the wizard
        // can surface them to the user if a future mode opts in.
        const mode = this.#state.value.uploadMode;
        const removedOpsCount = mode === 'replace' ? diff.removed.length : 0;
        const totalOps = diff.added.length + diff.updated.length + removedOpsCount;

        this.#dispatch(startUploadApply(totalOps));

        // Progress = streamed/acked adds + settled update/remove ops. The
        // reducer caps `completed` at `total`, so over-counting is safe.
        let addsCompleted = 0;
        let opsCompleted = 0;
        const pushProgress = (): void => this.#dispatch(advanceUploadProgress(addsCompleted + opsCompleted));

        // Subscribe to the live upload-item stream so adds advance the bar
        // as the backend confirms each row. Best-effort: when the socket
        // settings / token aren't available (e.g. unit tests) we fall back
        // to a single jump by the ack's `accepted` count.
        let stream: ReturnType<typeof openInventoryUploadStream> | null = null;
        // Streamed failures are pool-scoped on the wire; only ones whose
        // uploadId matches this apply's POST ack belong to this wizard.
        // Errors can outrun the ack, so they buffer until the id is known.
        let ackUploadId: string | null = null;
        let batchSettledByError = false;
        // The most recent claimed failure; reconciles the done summary so
        // it cannot report rows the backend just said failed to land.
        // Boxed because the writes happen in stream callbacks, which
        // control-flow analysis would otherwise narrow away at the reads.
        const claimedStreamError: { current: InventoryUploadError | null } = { current: null };
        const pendingStreamErrors: InventoryUploadError[] = [];
        const reportStreamError = (error: InventoryUploadError): void => {
            batchSettledByError = true;
            claimedStreamError.current = error;
            this.#dispatch(
                pushUploadStreamError({
                    reason: error.reason,
                    accepted: error.accepted,
                    failed: error.failed,
                }),
            );
        };
        if (diff.added.length > 0) {
            let streamCtx: { uri: string; token: string | undefined } | null = null;
            try {
                const state = this.#store.getState();
                const settings = getLocatesSocketConnectionSettingsFromState(state);
                if (settings) {
                    let token: string | undefined;
                    try {
                        token = getLocatesAuthPayloadFromState(state);
                    } catch {
                        token = undefined;
                    }
                    streamCtx = { uri: settings.uri, token };
                }
            } catch {
                streamCtx = null;
            }
            if (streamCtx) {
                stream = openInventoryUploadStream({
                    uri: streamCtx.uri,
                    accessToken: streamCtx.token,
                    poolId,
                    // The stream is scoped to this pool's upload room and
                    // dedupes by item id, so every callback is a distinct
                    // added row. Advance the bar as they land, capped at the
                    // number we sent.
                    onItem: () => {
                        if (addsCompleted >= diff.added.length) return;
                        addsCompleted += 1;
                        pushProgress();
                    },
                    // Streamed failures feed the wizard's messages panel
                    // (PLAT-12803). The payload carries no prose — the view
                    // maps the reason to its own copy. Returning true (a
                    // claimed error) also releases the stream's item waiters.
                    onError: (error) => {
                        if (ackUploadId === null) {
                            pendingStreamErrors.push(error);
                            return false;
                        }
                        if (error.uploadId !== ackUploadId) return false;
                        reportStreamError(error);
                        return true;
                    },
                });
            }
        }

        const toastId = this.#startActionToast(
            t('admin::pool-management::toast-upload-in-progress', { count: totalOps }),
        );
        try {
            const ops: Promise<unknown>[] = [];

            // Held separately so the backend's accepted/rejected counts can be
            // read once the batch settles; `ops` only drives the failure check.
            let addBatchPromise: Promise<AddPoolItemsResponse> | null = null;
            if (diff.added.length > 0) {
                const items: NewPoolItem[] = diff.added.map((item) => ({
                    symbol: item.symbol,
                    quantity: item.quantity,
                    // The missing-price guard above already rejected the apply,
                    // so labPrice is guaranteed non-null at this point.
                    price: item.labPrice!,
                }));
                addBatchPromise = addPoolItems({ poolId, request: { items } });
                ops.push(addBatchPromise);
            }
            for (const upd of diff.updated) {
                ops.push(
                    updatePoolItem({
                        poolId,
                        itemId: upd.rowId,
                        request: { quantity: upd.toQuantity, price: upd.toLabPrice },
                    }).finally(() => {
                        opsCompleted += 1;
                        pushProgress();
                    }),
                );
            }
            if (mode === 'replace') {
                for (const rem of diff.removed) {
                    ops.push(
                        removePoolItem({ poolId, itemId: rem.rowId }).finally(() => {
                            opsCompleted += 1;
                            pushProgress();
                        }),
                    );
                }
            }

            const results = await Promise.allSettled(ops);
            const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
            if (failures.length > 0) {
                // Surface any partially-applied rows before reporting the error.
                await Promise.all([
                    invalidatePoolItemsQuery(this.#queryClient, poolId),
                    invalidatePoolChangeLogQuery(this.#queryClient, poolId),
                ]);
                this.#markFresh();
                const first = failures[0]!.reason;
                const message = first instanceof Error ? first.message : String(first);
                this.#dispatch(failUploadApply(message));
                this.#replaceToast(toastId, {
                    variant: 'error',
                    message: t('admin::pool-management::toast-upload-error', { message }),
                });
                return false;
            }

            // Read the backend's accepted/rejected counts. The batch already
            // settled without rejecting (checked above), so this resolves
            // immediately. `accepted` is the rows that passed validation;
            // `rejected` were dropped up front for an invalid price/quantity.
            // With no adds, fall back to the attempted count (0 rejected).
            const addResult = addBatchPromise ? await addBatchPromise : null;
            const addedCount = addResult ? addResult.accepted : diff.added.length;
            const rejectedCount = addResult ? addResult.rejected : 0;
            if (addResult) {
                // The ack names this apply's uploadId; flush any failures
                // that streamed in before it resolved.
                ackUploadId = addResult.uploadId;
                for (const error of pendingStreamErrors.splice(0)) {
                    if (error.uploadId === ackUploadId) reportStreamError(error);
                }
            }

            // Reconcile the live progress bar to what the backend accepted:
            // with a stream, wait for the streamed items to catch up (bounded
            // by an idle window so a dropped event can't hang the wizard);
            // otherwise jump the bar by `accepted`.
            if (addResult) {
                const expectedAdds = Math.min(addResult.accepted, diff.added.length);
                // Only rely on the stream once it has actually authenticated
                // and joined the room; otherwise a never-ready socket would
                // strand the bar in `waitForItems` until the idle timeout.
                const streamReady = stream ? await stream.whenReady(UPLOAD_STREAM_READY_MS) : false;
                // A failure that already settled the batch means the missing
                // items will never stream — jump the bar instead of waiting.
                if (stream && streamReady && !batchSettledByError) {
                    const waitOutcome = await stream.waitForItems(expectedAdds, UPLOAD_STREAM_IDLE_MS);
                    // Idling out short of the expected count with no claimed
                    // error is exactly the shape of a persistence failure
                    // whose error event hasn't landed yet — hold the
                    // listener open briefly rather than tearing it down
                    // mid-arrival and reporting a false success.
                    if (waitOutcome === 'idle' && !batchSettledByError) {
                        await stream.waitForError(UPLOAD_STREAM_ERROR_GRACE_MS);
                    }
                } else {
                    stream?.[Symbol.dispose]();
                    stream = null;
                    addsCompleted = expectedAdds;
                    pushProgress();
                }
            }

            // Fill the bar to 100% and hold briefly so the apply reads as
            // visually complete before the done screen replaces it. Dispose
            // the stream first so a late item can't tug the bar back down.
            stream?.[Symbol.dispose]();
            stream = null;
            this.#dispatch(advanceUploadProgress(totalOps));
            await new Promise<void>((resolve) => setTimeout(resolve, UPLOAD_COMPLETE_HOLD_MS));

            // Rows are inserted asynchronously (they arrive over the stream),
            // so refetch now the stream has confirmed them (or it went idle)
            // so the table shows the applied rows without a manual refresh.
            await Promise.all([
                invalidatePoolItemsQuery(this.#queryClient, poolId),
                invalidatePoolChangeLogQuery(this.#queryClient, poolId),
            ]);
            this.#markFresh();

            // Upload supersedes any pending local adds for the pool.
            if (this.#pendingAddsByPool.value.has(poolId)) {
                const map = new Map(this.#pendingAddsByPool.value);
                map.delete(poolId);
                this.#pendingAddsByPool.value = map;
            }

            // A claimed streamed failure overrides the ack's accepted count —
            // the ack only reflects validation, not persistence. A partial
            // failure reports the rows that actually landed; a fatal one
            // (pool-locked / upload-failed) means none did. The update /
            // remove ops settle over HTTP individually, so their counts
            // stand either way.
            const persistedAdds =
                claimedStreamError.current === null
                    ? addedCount
                    : claimedStreamError.current.reason === InventoryUploadErrorReason.ItemsFailed
                      ? Math.min(claimedStreamError.current.accepted, addedCount)
                      : 0;

            // SUCCEED_UPLOAD_APPLY sets uploadStep='done', clears progress,
            // and clears applying/error in one reducer transition. The summary
            // reports what the backend actually accepted; in merge mode
            // `removed` is always 0 because no DELETE was issued.
            this.#dispatch(
                succeedUploadApply({
                    added: persistedAdds,
                    updated: diff.updated.length,
                    removed: removedOpsCount,
                    rejected: rejectedCount,
                }),
            );
            this.#replaceToast(
                toastId,
                claimedStreamError.current !== null
                    ? {
                          variant: 'error',
                          message: t('admin::pool-management::toast-upload-problems'),
                      }
                    : {
                          variant: 'success',
                          message:
                              rejectedCount > 0
                                  ? t('admin::pool-management::toast-upload-success-with-rejected', {
                                        added: persistedAdds,
                                        updated: diff.updated.length,
                                        removed: removedOpsCount,
                                        rejected: rejectedCount,
                                    })
                                  : t('admin::pool-management::toast-upload-success', {
                                        added: persistedAdds,
                                        updated: diff.updated.length,
                                        removed: removedOpsCount,
                                    }),
                      },
            );
            return true;
        } catch (e) {
            const message = e instanceof Error ? e.message : t('admin::pool-management::upload-error-apply-failed');
            this.#dispatch(failUploadApply(message));
            this.#replaceToast(toastId, {
                variant: 'error',
                message: t('admin::pool-management::toast-upload-error', { message }),
            });
            return false;
        } finally {
            stream?.[Symbol.dispose]();
        }
    }
    downloadInventoryTemplate(): void {
        if (typeof document === 'undefined') return;
        const csv = createInventoryTemplateCsv();
        this.#triggerCsvDownload(csv, 'inventory-template.csv');
    }
    async downloadCurrentPoolCsv(): Promise<void> {
        if (typeof document === 'undefined') return;
        const pool = this.selectedPool.value;
        if (!pool) return;
        // Guard re-entrancy: the button also disables on this flag, but a
        // keyboard-repeat or programmatic caller could still fire twice.
        if (this.#state.value.isDownloadingCsv) return;
        // Flag in-flight so the toolbar can disable the button and spin a
        // loader; the fetch below can take a moment on a large pool.
        this.#dispatch(startCsvDownload());
        try {
            // The user expects the full pool, not just the current page.
            // Gather it via real backend pagination; the cache key is shared
            // with the model's "all rows" / substring-search fetches, so this
            // usually completes against an already-warm entry.
            const allOptions = getAllPoolItemsQuery(pool.id);
            const response = await this.#queryClient.fetchQuery({
                ...allOptions,
                queryKey: allOptions.queryKey ?? [],
            });
            // Drop rows the user has staged for removal: keeping them in the
            // export would let them re-upload the file and silently undo the
            // pending delete. Drafts (pending adds + field edits) aren't in
            // the full-pool server response anyway, so this only needs to
            // filter the pending-removal set.
            const pendingRemovals = this.pendingRowRemovals.value;
            const rows = response.items.map(fromApiItem).filter((row) => !pendingRemovals.has(row.id));
            const csv = createInventoryCsv(rows);
            // Filename pattern: `Retail_Pool_2026-04-29.csv`. The shared
            // `sanitizeFilenameSegment` (see top of file) collapses
            // whitespace and strips filesystem-reserved / control
            // characters (Windows is the strictest with `<>:"/\|?*`) so
            // the download dialog stays single-token. Date is the local
            // ISO yyyy-mm-dd so two exports on the same day overwrite
            // cleanly in the Downloads dir.
            const safeName = sanitizeFilenameSegment(pool.name) || 'pool';
            const today = new Date();
            const yyyy = today.getFullYear();
            const mm = String(today.getMonth() + 1).padStart(2, '0');
            const dd = String(today.getDate()).padStart(2, '0');
            this.#triggerCsvDownload(csv, `${safeName}_${yyyy}-${mm}-${dd}.csv`);
        } finally {
            this.#dispatch(endCsvDownload());
        }
    }

    async downloadChangeLogCsv(): Promise<void> {
        if (typeof document === 'undefined') return;
        const pool = this.selectedPool.value;
        if (!pool) return;
        // Export the entire filtered audit trail, not just the current page.
        // Gather it via the all-rows change-log query with the active filter
        // (same cache key as the tab's "all" mode, so it's often already warm);
        // the timeline's day grouping isn't carried into the CSV.
        const filter = buildChangeLogFilter(this.#state.value, this.changeLogActionFilter.value);
        const allOptions = getAllPoolChangeLogQuery(pool.id, { filter });
        const response = await this.#queryClient.fetchQuery({
            ...allOptions,
            queryKey: allOptions.queryKey ?? [],
        });
        const entries = response.entries.map(fromApiChangeLogEntry).sort((a, b) => b.timestamp - a.timestamp);
        if (entries.length === 0) return;
        const csv = createChangeLogCsv(entries, {
            columnLabels: {
                timestamp: t('admin::pool-management::changelog-export-col-timestamp'),
                user: t('admin::pool-management::changelog-export-col-user'),
                symbol: t('admin::pool-management::changelog-export-col-symbol'),
                action: t('admin::pool-management::changelog-export-col-action'),
                quantity: t('admin::pool-management::changelog-export-col-quantity'),
                price: t('admin::pool-management::changelog-export-col-price'),
                orderId: t('admin::pool-management::changelog-export-col-order-id'),
            },
        });
        const safeName = sanitizeFilenameSegment(pool.name) || 'pool';
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        this.#triggerCsvDownload(csv, `${safeName}_change-log_${yyyy}-${mm}-${dd}.csv`);
    }

    downloadPoolListCsv(): void {
        if (typeof document === 'undefined') return;
        const pools = this.filteredPools.value;
        if (pools.length === 0) return;
        // Firm-scoped views have no firm-name map; fall back to the scoped firm's own name.
        const firmId = this.#firmId.value;
        const firmName = this.#firmName.value;
        const firmNamesByFirmId = this.firmNamesByFirmId.value ?? (firmId && firmName ? { [firmId]: firmName } : null);
        const columnLabels = {
            id: t('admin::pool-management::pool-list-export-col-id'),
            name: t('admin::pool-management::pool-list-export-col-name'),
            description: t('admin::pool-management::pool-list-export-col-description'),
            firm: t('admin::pool-management::pool-list-export-col-firm'),
            status: t('admin::pool-management::pool-list-export-col-status'),
            permission: t('admin::pool-management::pool-list-export-col-permission'),
        } satisfies PoolListCsvColumnLabels;
        const csv = createPoolListCsv(pools, { columnLabels, firmNamesByFirmId });
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        this.#triggerCsvDownload(csv, `pools_${yyyy}-${mm}-${dd}.csv`);
    }

    #triggerCsvDownload(csv: string, filename: string): void {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        // Defer revocation: synchronous revoke can race with the browser's
        // download initiation in Firefox and Safari and abort the download.
        setTimeout(() => URL.revokeObjectURL(url), 0);
    }

    async refresh(): Promise<void> {
        const firmId = this.#firmId.value;
        await invalidatePoolsQuery(this.#queryClient, firmId ?? undefined);
        const poolId = this.#state.value.selectedPoolId;
        if (poolId) {
            await Promise.all([
                invalidatePoolItemsQuery(this.#queryClient, poolId),
                invalidatePoolChangeLogQuery(this.#queryClient, poolId),
            ]);
        }
        this.#markFresh();
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
